<template>
  <div class="teaser-page" :class="{ anim: isAnim }">
    <!-- AGE GATE -->
    <ConfirmAge1 v-if="showConfirm" :lang="currentLang" @lang-change="setLang" @confirmed="onConfirmed" />

    <a class="sr-only" href="#apply">{{ t('skip') }}</a>

    <!-- HEADER -->
    <transition name="header-fade">
      <header class="header on-dark" v-if="!showConfirm">
        <div class="in">
          <span class="logo" role="img" aria-label="Jameson"></span>
          <div class="right">
            <nav class="lang" :aria-label="t('lang_aria')">
              <a href="#" :aria-current="currentLang === 'ru' ? 'true' : null" @click.prevent="setLang('ru')">RU</a>
              <a href="#" :aria-current="currentLang === 'kz' ? 'true' : null" @click.prevent="setLang('kz')">KZ</a>
            </nav>
          </div>
        </div>
      </header>
    </transition>

    <main>
      <!-- HERO -->
      <section class="hero on-dark" aria-labelledby="heroTitle">
        <div class="hero-grid">
          <div class="hero-text">
            <h1 id="heroTitle">
              <img class="tt-title" :src="require('@/assets/img/teaser-title.webp')" width="1400" height="194" alt="Triple Trip">
              <span class="h1-text">{{ t('h1') }}</span>
            </h1>
            <p class="lead">{{ t('lead') }}</p>
            <div class="actions">
              <a class="btn btn-cream btn-lg" href="#apply" @click.prevent="scrollToApply('hero')">{{ t('cta_fill') }}</a>
              <a class="btn btn-outline btn-lg" href="#how" @click.prevent="scrollToHow('hero')">{{ t('cta_more') }}</a>
            </div>
          </div>
          <div class="hero-media">
            <div class="kv-stage" ref="kvStage" role="img" :aria-label="t('kv_aria')">
              <div class="kv-layers" ref="kvLayers" aria-hidden="true" :class="{ intro: isIntro }">
                <div class="kv-layer kv-l-bg" data-depth="0">
                  <img class="kv-bg" :src="require('@/assets/img/teaser-kv-bg.webp')" alt="Background">
                </div>
                <div class="kv-layer" data-depth="0.25">
                  <svg class="kv-pattern" focusable="false">
                    <defs>
                      <symbol id="jLetter" viewBox="54.33 55.47 194.31 289.07">
                        <path d="M 128.074219 290.46875 C 129.972656 291.421875 132.175781 291.785156 134.390625 291.398438 C 134.472656 291.382812 134.542969 291.324219 134.566406 291.246094 C 137.496094 282.761719 137.566406 272.054688 132.613281 261.980469 C 128.296875 253.183594 122.011719 248.003906 114.8125 245.589844 C 114.6875 245.546875 114.605469 245.71875 114.714844 245.789062 C 119.359375 248.933594 123.226562 253.277344 125.921875 258.777344 C 131.136719 269.378906 130.957031 280.738281 127.734375 289.625 C 127.613281 289.953125 127.761719 290.3125 128.074219 290.46875 "/>
                        <path d="M 181.488281 55.570312 L 181.648438 56.175781 C 181.660156 56.21875 181.644531 56.269531 181.605469 56.296875 C 174.640625 61.042969 167.898438 73.703125 171.785156 88.207031 C 176.152344 104.515625 194.535156 173.117188 200.9375 197.007812 L 207.914062 223.050781 C 212.296875 239.414062 218.390625 248.496094 226.835938 249.738281 C 226.875 249.742188 226.910156 249.769531 226.921875 249.808594 L 227.15625 250.453125 C 227.171875 250.492188 227.164062 250.539062 227.132812 250.570312 C 222.746094 254.851562 219.164062 265.449219 214.636719 275.179688 C 210.03125 285.066406 200.367188 315.164062 163.585938 329.222656 C 123.5 344.539062 82.753906 334.875 65.566406 306.503906 C 54.332031 288.019531 57.019531 262.738281 75.28125 252.5 C 91.132812 243.628906 108.21875 249.441406 114.648438 262.570312 C 120.894531 275.265625 116.589844 288.816406 111.4375 293.269531 C 110.390625 294.203125 111.410156 295.984375 113.832031 297.613281 C 124.523438 304.964844 136.664062 304.5625 148.398438 298.363281 C 163.414062 290.441406 168.761719 276.824219 160.972656 247.753906 C 154.574219 223.871094 126.1875 117.925781 121.8125 101.597656 C 117.925781 87.09375 105.753906 79.5 97.347656 78.871094 C 97.300781 78.867188 97.261719 78.835938 97.25 78.789062 L 97.089844 78.183594 C 97.074219 78.125 97.109375 78.0625 97.167969 78.046875 L 181.347656 55.488281 C 181.410156 55.472656 181.472656 55.507812 181.488281 55.570312 "/>
                        <path d="M 233.613281 260.9375 C 228.835938 272.988281 221.820312 290.351562 213.9375 302.808594 C 213.871094 302.914062 214.003906 303.035156 214.101562 302.957031 C 222.746094 296.078125 231.476562 282.636719 236.308594 270.625 C 239.382812 262.980469 245.910156 247.214844 248.519531 244.566406 C 248.640625 244.445312 248.582031 244.242188 248.417969 244.195312 C 243.394531 242.707031 232.777344 241.429688 226.519531 218.066406 C 226.519531 218.066406 195.164062 101.0625 190.796875 84.753906 C 187.367188 71.953125 190.378906 64.183594 194.90625 59.066406 C 194.980469 58.984375 194.898438 58.851562 194.789062 58.886719 C 186.386719 61.515625 180.871094 73.972656 184.699219 88.265625 C 189.070312 104.570312 219.953125 219.828125 219.953125 219.828125 C 222 227.464844 224.652344 233.449219 227.761719 237.789062 C 230.804688 242.035156 235.011719 245.289062 239.117188 246.148438 C 239.519531 246.234375 240.160156 246.296875 240.613281 246.332031 C 240.808594 246.347656 240.886719 246.585938 240.746094 246.722656 C 240.527344 246.9375 240.308594 247.1875 240.085938 247.46875 C 238.113281 249.96875 235.902344 255.160156 233.613281 260.9375 "/>
                      </symbol>
                      <pattern id="jPattern" width="104" height="132" patternUnits="userSpaceOnUse">
                        <use href="#jLetter" x="14" y="14" width="26" height="38.7"/>
                        <use href="#jLetter" x="66" y="80" width="26" height="38.7"/>
                      </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#jPattern)"/>
                  </svg>
                </div>
                <div class="kv-layer" data-depth="0.5"><div class="kv-glow"></div></div>
                <div class="kv-layer" data-depth="1">
                  <svg class="kv-j" viewBox="54.33 55.47 216.68 289.07" focusable="false">
                    <path d="M 128.074219 290.46875 C 129.972656 291.421875 132.175781 291.785156 134.390625 291.398438 C 134.472656 291.382812 134.542969 291.324219 134.566406 291.246094 C 137.496094 282.761719 137.566406 272.054688 132.613281 261.980469 C 128.296875 253.183594 122.011719 248.003906 114.8125 245.589844 C 114.6875 245.546875 114.605469 245.71875 114.714844 245.789062 C 119.359375 248.933594 123.226562 253.277344 125.921875 258.777344 C 131.136719 269.378906 130.957031 280.738281 127.734375 289.625 C 127.613281 289.953125 127.761719 290.3125 128.074219 290.46875 "/>
                    <path d="M 181.488281 55.570312 L 181.648438 56.175781 C 181.660156 56.21875 181.644531 56.269531 181.605469 56.296875 C 174.640625 61.042969 167.898438 73.703125 171.785156 88.207031 C 176.152344 104.515625 194.535156 173.117188 200.9375 197.007812 L 207.914062 223.050781 C 212.296875 239.414062 218.390625 248.496094 226.835938 249.738281 C 226.875 249.742188 226.910156 249.769531 226.921875 249.808594 L 227.15625 250.453125 C 227.171875 250.492188 227.164062 250.539062 227.132812 250.570312 C 222.746094 254.851562 219.164062 265.449219 214.636719 275.179688 C 210.03125 285.066406 200.367188 315.164062 163.585938 329.222656 C 123.5 344.539062 82.753906 334.875 65.566406 306.503906 C 54.332031 288.019531 57.019531 262.738281 75.28125 252.5 C 91.132812 243.628906 108.21875 249.441406 114.648438 262.570312 C 120.894531 275.265625 116.589844 288.816406 111.4375 293.269531 C 110.390625 294.203125 111.410156 295.984375 113.832031 297.613281 C 124.523438 304.964844 136.664062 304.5625 148.398438 298.363281 C 163.414062 290.441406 168.761719 276.824219 160.972656 247.753906 C 154.574219 223.871094 126.1875 117.925781 121.8125 101.597656 C 117.925781 87.09375 105.753906 79.5 97.347656 78.871094 C 97.300781 78.867188 97.261719 78.835938 97.25 78.789062 L 97.089844 78.183594 C 97.074219 78.125 97.109375 78.0625 97.167969 78.046875 L 181.347656 55.488281 C 181.410156 55.472656 181.472656 55.507812 181.488281 55.570312 "/>
                    <path d="M 233.613281 260.9375 C 228.835938 272.988281 221.820312 290.351562 213.9375 302.808594 C 213.871094 302.914062 214.003906 303.035156 214.101562 302.957031 C 222.746094 296.078125 231.476562 282.636719 236.308594 270.625 C 239.382812 262.980469 245.910156 247.214844 248.519531 244.566406 C 248.640625 244.445312 248.582031 244.242188 248.417969 244.195312 C 243.394531 242.707031 232.777344 241.429688 226.519531 218.066406 C 226.519531 218.066406 195.164062 101.0625 190.796875 84.753906 C 187.367188 71.953125 190.378906 64.183594 194.90625 59.066406 C 194.980469 58.984375 194.898438 58.851562 194.789062 58.886719 C 186.386719 61.515625 180.871094 73.972656 184.699219 88.265625 C 189.070312 104.570312 219.953125 219.828125 219.953125 219.828125 C 222 227.464844 224.652344 233.449219 227.761719 237.789062 C 230.804688 242.035156 235.011719 245.289062 239.117188 246.148438 C 239.519531 246.234375 240.160156 246.296875 240.613281 246.332031 C 240.808594 246.347656 240.886719 246.585938 240.746094 246.722656 C 240.527344 246.9375 240.308594 247.1875 240.085938 247.46875 C 238.113281 249.96875 235.902344 255.160156 233.613281 260.9375 "/>
                    <path d="M 244.96875 220.457031 L 244.96875 208.839844 L 240.605469 208.839844 L 240.605469 205.621094 L 252.949219 205.621094 L 252.949219 208.839844 L 248.582031 208.839844 L 248.582031 220.457031 L 244.96875 220.457031 "/>
                    <path d="M 267.394531 220.457031 L 267.394531 209.234375 L 263.902344 220.457031 L 261.867188 220.457031 L 258.332031 209.234375 L 258.332031 220.457031 L 255.109375 220.457031 L 255.109375 205.621094 L 260.617188 205.621094 L 263.132812 214.890625 L 265.792969 205.621094 L 271.007812 205.621094 L 271.007812 220.457031 L 267.394531 220.457031 "/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
        <a class="scroll-cue" href="#how" @click.prevent="scrollToHow">
          <span>{{ t('cue') }}</span><i aria-hidden="true"></i>
        </a>
      </section>

      <!-- HOW -->
      <section class="how" id="how" aria-labelledby="howTitle">
        <div class="wrap">
          <div class="shead">
            <h2 id="howTitle">{{ t('how_title') }}</h2>
            <p>{{ t('how_sub') }}</p>
          </div>
          <ol class="steps">
            <li class="step">
              <span class="ico-par">
                <svg class="ico" viewBox="0 0 88 88" aria-hidden="true">
                  <defs>
                    <linearGradient id="gold1" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0" stop-color="#C6A15B"/>
                      <stop offset=".45" stop-color="#E4C88A"/>
                      <stop offset="1" stop-color="#B88A3E"/>
                    </linearGradient>
                  </defs>
                  <circle cx="44" cy="44" r="44" fill="#008557"/>
                  <rect x="26" y="22" width="36" height="46" rx="3" fill="none" stroke="#FFF8E5" stroke-width="3"/>
                  <rect x="34" y="17" width="20" height="10" rx="2" fill="url(#gold1)"/>
                  <path d="M33 38h22M33 46h22M33 54h12" stroke="#FFF8E5" stroke-width="3" stroke-linecap="round"/>
                  <path d="M58 50l8-8 5 5-8 8-7 2z" fill="url(#gold1)" stroke="#00483A" stroke-width="1.5" stroke-linejoin="round"/>
                </svg>
              </span>
              <span class="num">{{ t('step1') }}</span>
              <h3>{{ t('s1_t') }}</h3>
              <p>{{ t('s1_p') }}</p>
              <div class="foot">
                <a class="btn btn-how" href="#apply" @click.prevent="scrollToApply('how')">{{ t('cta_fill') }}</a>
              </div>
            </li>
            <li class="step">
              <span class="ico-par">
                <svg class="ico" viewBox="0 0 88 88" aria-hidden="true">
                  <defs>
                    <linearGradient id="gold2" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0" stop-color="#C6A15B"/>
                      <stop offset=".45" stop-color="#E4C88A"/>
                      <stop offset="1" stop-color="#B88A3E"/>
                    </linearGradient>
                  </defs>
                  <circle cx="44" cy="44" r="44" fill="#008557"/>
                  <rect x="18" y="28" width="46" height="34" rx="3" fill="none" stroke="#FFF8E5" stroke-width="3"/>
                  <path d="M19 30l22 17 22-17" fill="none" stroke="#FFF8E5" stroke-width="3" stroke-linejoin="round"/>
                  <rect x="52" y="44" width="22" height="22" rx="3" fill="url(#gold2)" stroke="#00483A" stroke-width="1.5"/>
                  <rect x="52" y="44" width="22" height="7" fill="#00483A"/>
                  <path d="M57 41v6M69 41v6" stroke="#00483A" stroke-width="2.5" stroke-linecap="round"/>
                  <text x="63" y="62.5" text-anchor="middle" font-family="JJ,Arial,sans-serif" font-weight="700" font-size="11" fill="#00483A">1</text>
                </svg>
              </span>
              <span class="num">{{ t('step2') }}</span>
              <h3>{{ t('s2_t') }}</h3>
              <p>{{ t('s2_p') }}</p>
              <div class="foot">
                <p class="date"><time datetime="2026-11-01">{{ t('date') }}</time></p>
              </div>
            </li>
          </ol>
        </div>
      </section>

      <!-- APPLY -->
      <section class="apply on-dark" id="apply" aria-labelledby="applyTitle">
        <div class="wrap">
          <div class="shead">
            <h2 id="applyTitle">{{ t('apply_title') }}</h2>
            <p v-html="t('apply_sub')"></p>
          </div>

          <div class="agent" id="agent">
            <div class="agent-head">
              <span class="logo" role="img" aria-label="Jameson"></span>
              <span class="t" id="agentStatus">{{ statusMessage }}</span>
              <div class="agent-progress" aria-hidden="true">
                <div class="agent-progress-lbl" id="progLbl">{{ progressText }}</div>
                <div class="agent-progress-bar">
                  <i id="progBar" :style="{ width: progressPercent + '%' }" :class="{ complete: filledCount === 7 }"></i>
                </div>
              </div>
            </div>

            <form class="classic" id="classic" novalidate :aria-label="t('form_aria')" @submit.prevent="submitForm">
              <div class="grid2">
                <!-- First Name -->
                <div class="cf" :class="{ bad: errors.first_name }">
                  <label for="c_first">{{ t('l_first') }}</label>
                  <input
                    class="field"
                    id="c_first"
                    v-model="formData.first_name"
                    autocomplete="given-name"
                    aria-describedby="ce_first_name"
                    @focus="onFieldFocus('first_name')"
                    @input="validateField('first_name')"
                  >
                  <p class="err" id="ce_first_name" :class="{ on: errors.first_name }" role="alert">
                    {{ errors.first_name }}
                  </p>
                </div>

                <!-- Last Name -->
                <div class="cf" :class="{ bad: errors.last_name }">
                  <label for="c_last">{{ t('l_last') }}</label>
                  <input
                    class="field"
                    id="c_last"
                    v-model="formData.last_name"
                    autocomplete="family-name"
                    aria-describedby="ce_last_name"
                    @focus="onFieldFocus('last_name')"
                    @input="validateField('last_name')"
                  >
                  <p class="err" id="ce_last_name" :class="{ on: errors.last_name }" role="alert">
                    {{ errors.last_name }}
                  </p>
                </div>

                <!-- Email -->
                <div class="cf" :class="{ bad: errors.email }">
                  <label for="c_email">{{ t('l_email') }}</label>
                  <input
                    class="field"
                    id="c_email"
                    type="email"
                    inputmode="email"
                    autocomplete="email"
                    placeholder="name@mail.kz"
                    v-model="formData.email"
                    aria-describedby="ce_email"
                    @focus="onFieldFocus('email')"
                    @input="validateField('email')"
                  >
                  <p class="err" id="ce_email" :class="{ on: errors.email }" role="alert">
                    {{ errors.email }}
                  </p>
                </div>

                <!-- Phone -->
                <div class="cf" :class="{ bad: errors.phone }">
                  <label for="c_phone">{{ t('l_phone') }}</label>
                  <input
                    class="field"
                    id="c_phone"
                    type="tel"
                    inputmode="tel"
                    autocomplete="tel"
                    placeholder="+7 (___) ___-__-__"
                    v-model="formData.phone"
                    v-mask="'+7 (###) ###-##-##'"
                    aria-describedby="ce_phone"
                    @focus="onPhoneFocus"
                    @input="onPhoneInput"
                  >
                  <p class="err" id="ce_phone" :class="{ on: errors.phone }" role="alert">
                    {{ errors.phone }}
                  </p>
                </div>

                <!-- Gender -->
                <div class="cf full" :class="{ bad: errors.gender }">
                  <span class="lbl" id="c_gl">{{ t('l_gender') }}</span>
                  <div class="seg" role="radiogroup" aria-labelledby="c_gl">
                    <label>
                      <input
                        type="radio"
                        name="c_gender"
                        value="Мужской"
                        v-model="formData.gender"
                        @focus="onFieldFocus('gender')"
                        @change="validateField('gender')"
                      >
                      <span>{{ t('g_m') }}</span>
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="c_gender"
                        value="Женский"
                        v-model="formData.gender"
                        @focus="onFieldFocus('gender')"
                        @change="validateField('gender')"
                      >
                      <span>{{ t('g_f') }}</span>
                    </label>
                  </div>
                  <p class="err" id="ce_gender" :class="{ on: errors.gender }" role="alert">
                    {{ errors.gender }}
                  </p>
                </div>

                <!-- City -->
                <div class="cf full" :class="{ bad: errors.city }">
                  <label for="c_city">{{ t('l_city') }}</label>
                  <select
                    class="field"
                    id="c_city"
                    v-model="formData.city"
                    aria-describedby="ce_city"
                    @focus="onFieldFocus('city')"
                    @change="validateField('city')"
                  >
                    <option value="">{{ t('city_ph') }}</option>
                    <!-- Ответы из CRM анкеты -->
                    <template v-if="crmCities && crmCities.length > 0">
                      <option
                        v-for="c in crmCities"
                        :key="c.id || c.name || c"
                        :value="c.name || c"
                      >
                        {{ cityLabel(c.name || c) }}
                      </option>
                    </template>
                    <!-- Резервный список (пока CRM загружается или если ответы пусты) -->
                    <template v-else>
                      <optgroup :label="t('popular')">
                        <option v-for="c in popularCities" :key="c" :value="c">
                          {{ cityLabel(c) }}
                        </option>
                      </optgroup>
                      <optgroup v-for="o in oblasts" :key="o[0]" :label="oblastLabel(o[0])">
                        <option v-for="c in o[2]" :key="c" :value="c">
                          {{ cityLabel(c) }}
                        </option>
                      </optgroup>
                    </template>
                  </select>
                  <p class="err" id="ce_city" :class="{ on: errors.city }" role="alert">
                    {{ errors.city }}
                  </p>
                </div>

                <!-- Consent -->
                <div class="cf full" :class="{ bad: errors.consent }">
                  <label class="consent" id="c_consentBox">
                    <input
                      type="checkbox"
                      id="c_consent"
                      v-model="formData.consent"
                      aria-describedby="ce_consent"
                      @focus="onFieldFocus('consent')"
                      @change="validateField('consent')"
                    >
                    <span @click="handleConsentClick" v-html="t('consent')"></span>
                  </label>
                  <p class="err" id="ce_consent" :class="{ on: errors.consent }" role="alert">
                    {{ errors.consent }}
                  </p>
                </div>

                <!-- Global Alert message if any -->
                <div class="cf full" v-if="globalAlert">
                  <div class="alert" role="alert">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M12 7v6M12 17h.01"/>
                    </svg>
                    <div>
                      <b>{{ globalAlert.title }}</b>
                      <p>{{ globalAlert.text }}</p>
                    </div>
                  </div>
                </div>

                <!-- Submit Button -->
                <div class="cf full">
                  <button
                    class="btn btn-dark btn-lg"
                    id="c_send"
                    type="submit"
                    style="width:100%"
                    :disabled="isSending || isSendSms"
                    :aria-busy="isSending || isSendSms ? 'true' : null"
                  >
                    {{ isSending || isSendSms ? t('sending') : (hasSubmittedOnce ? t('send_again') : t('send')) }}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>
    </main>

    <!-- FOOTER -->
    <footer class="on-dark">
      <div class="wrap">
        <div class="r1">
          <span class="logo" role="img" aria-label="Jameson"></span>
          <p class="resp">{{ t('resp') }}</p>
        </div>
        <div class="r2">
          <nav class="links" :aria-label="t('legal_aria')">
            <a href="#" @click.prevent="openPopup('policy')">{{ t('pp') }}</a>
            <!-- <a href="#" @click.prevent="openPopup('terms')">{{ t('terms') }}</a> -->
            <a href="https://responsibledrinking.eu/" target="_blank" rel="noopener">{{ t('resp_link') }}</a>
          </nav>
          <a class="ig" href="https://www.instagram.com/jamesonkz/" target="_blank" rel="noopener" :aria-label="t('ig')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="5"/>
              <circle cx="12" cy="12" r="4"/>
              <circle cx="17.5" cy="6.5" r="1" fill="currentColor"/>
            </svg>
          </a>
        </div>
        <p class="legal">{{ t('legal') }}</p>
      </div>
    </footer>

    <!-- SUCCESS MODAL POPUP -->
    <transition name="teaser-modal">
      <div class="done-modal" v-if="showDoneModal">
        <div class="done-modal-backdrop" @click="closeDoneModal"></div>
        <div class="done-modal-card" role="dialog" aria-modal="true" aria-labelledby="doneTitle">
          <button class="done-modal-x" type="button" @click="closeDoneModal" :aria-label="t('close')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18"/>
            </svg>
          </button>
          <div class="done-ok" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
              <path d="M5 13l4 4L19 7"/>
            </svg>
          </div>
          <h3 id="doneTitle">{{ t('done_title') }}</h3>
          <p>{{ doneEmailText }}</p>
          <p class="done-region" v-if="doneRegionText">{{ doneRegionText }}</p>
          <button class="btn btn-dark btn-lg done-modal-ok" type="button" @click="closeDoneModal">
            {{ t('ok') }}
          </button>
        </div>
      </div>
    </transition>

    <!-- LEGAL POPUP (Политика конфиденциальности, Условия использования, Согласие) -->
    <transition name="legal-popup">
      <div v-if="isLegalPopupOpen" class="legal-popup-overlay" @click.self="closeLegalPopup">
        <div class="legal-popup-content">
          <div class="legal-popup-header">
            <span class="legal-header-text">{{ currentPopupData ? currentPopupData.title : '' }}</span>
            <button class="legal-popup-close" @click="closeLegalPopup" type="button" aria-label="Закрыть">
              <svg width="18" height="18" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M1.07373 33.4784C-0.348236 34.8712 -0.377256 37.4247 1.10275 38.9046C2.58275 40.3845 5.13648 40.3264 6.52942 38.9336L19.9945 25.4697L33.4597 38.9046C34.9106 40.3555 37.4063 40.3845 38.8863 38.8756C40.3663 37.3957 40.3663 34.9003 38.9154 33.4494L25.4793 20.0145L38.9154 6.5506C40.3663 5.09975 40.3663 2.60428 38.8863 1.12441C37.4063 -0.384476 34.9106 -0.355459 33.4597 1.09539L19.9945 14.5303L6.52942 1.06638C5.13648 -0.326442 2.58275 -0.384476 1.10275 1.09539C-0.377256 2.57526 -0.348236 5.12876 1.07373 6.5506L14.5388 20.0145L1.07373 33.4784Z"
                  fill="#F2E4B5"
                />
              </svg>
            </button>
          </div>
          <div class="legal-popup-body" v-if="currentPopupData" v-html="currentPopupData.content"></div>
        </div>
      </div>
    </transition>

    <!-- OTP VERIFICATION POPUP -->
    <otp
      :showPopup="showOtpPopup"
      :smsTimer="smsTimer"
      :errorSms="otpErrorSms"
      @resend="resendSms"
      @verifyCode="submitAnswersWithCode"
      @closePopup="handleOtpClose"
      :success="otpSuccess"
      :errorMessage="otpError"
      :loading="otpVerifying"
    />

    <!-- SNACKBAR NOTIFICATION -->
    <v-snackbar v-model="snackbar" timeout="4000" top right color="#00483A">
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" color="#FFF8E5" @click="snackbar = false">
          {{ t('close') }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mask } from "vue-the-mask";
import Otp from "@/components/otp.vue";
import ConfirmAge1 from "@/components/age-confirm.vue";
import { mapGetters } from "vuex";
import {
  initLanding,
  trackEvent,
  trackCtaClick,
  trackFormStart,
  trackFormStepComplete,
  trackGenerateLead,
  trackFormSubmitError,
  trackJsError,
  trackFormSubmit,
  trackFormError,
  trackRegistrationSuccess,
  trackVirtualPageView
} from "@/utils/analytics";

export default {
  name: "JamesonTeaser",
  directives: { mask },
  components: {
    Otp,
    ConfirmAge1
  },
  data() {
    let hasAge = false;
    try {
      hasAge = typeof window !== "undefined" && window.localStorage && window.localStorage.getItem("userAge") !== null;
    } catch (e) {
      console.warn("Storage access failed", e);
    }

    return {
      campaignId: "bff481e3-eecd-f111-b802-00155d79212a",
      showConfirm: !hasAge,
      currentLang: "ru",
      isAnim: false,
      isIntro: true,
      formStarted: false,
      isSending: false,
      isSendSms: false,
      hasSubmittedOnce: false,
      globalAlert: null,
      completedSteps: [],
      onJsError: null,
      onUnhandledRejection: null,

      // CRM schema state
      formSchema: null,
      isLoadingForm: false,
      formError: false,
      crmCities: [],

      // Notification snackbar
      snackbar: false,
      snackbarMessage: "",

      // OTP state
      showOtpPopup: false,
      smsTimer: 60,
      smsTimerInterval: null,
      otpSuccess: false,
      otpErrorSms: false,
      otpError: "",
      otpVerifying: false,

      // Done Modal
      showDoneModal: false,
      submittedData: null,
      isRegistrationCompleted: false,

      // Legal Popups (from SocialLead)
      isLegalPopupOpen: false,
      currentPopup: null,
      popupTexts: {
        policy: {
          title: "Политика конфиденциальности",
          content: `<b>1. Общие положения</b>
Настоящая политика конфиденциальности (далее – Политика) составлена в соответствии с требованиями Закона Республики Казахстан от 21 мая 2013 года №94-V «О персональных данных и их защите», иных нормативно-правовых актов, регулирующих требования к сбору, хранению и обработки персональных данных, и определяет порядок обработки персональных данных и меры по обеспечению безопасности персональных данных, предпринимаемые ТОО «Перно Рикар Казахстан» (далее – Компания).

1.1. Компания ставит своей первостепенной целью безопасность и конфиденциальность предоставленной пользователями веб-сайта <a href="https://jameson.prk-events.kz/teaser" target="_blank" rel="noopener">https://jameson.prk-events.kz/teaser</a> (далее – «Веб-сайт») личной информации. В соответствии с нормативными требованиями и стандартами, Настоящая Политика, основываясь на требованиях законодательства Республики Казахстан в области сбора, обработки, хранения и защиты персональных данных, подробно описывает личную информацию, которую Компания собирает, цели ее сбора и обработки, а также ваши права в отношении персональных данных.

1.2. Настоящая Политика применяется в отношении сбора, обработки, хранения, а также передачи персональных данных, которые Компания может получить о пользователях Веб-сайта.

<b>2. Основные понятия, используемые в Политике</b>
<b>2.1. Компания</b> – ТОО «Перно Рикар Казахстан», место нахождения: А26T8F4, Республика Казахстан, г. Алматы, Медеуский район, ул. Рубинштейна, дом 48.
<b>2.2. Автоматизированная обработка персональных данных</b> – обработка персональных данных с использованием средств вычислительной техники.
<b>2.3. Блокирование персональных данных</b> – временное прекращение обработки персональных данных.
<b>2.4. Веб-сайт</b> – интернет-сайт, расположенный по следующему адресу: <a href="https://jameson.prk-events.kz/teaser" target="_blank" rel="noopener">https://jameson.prk-events.kz/teaser</a>.
<b>2.5. Информационная система персональных данных</b> – совокупность содержащихся в базах данных персональных данных и обеспечивающих их обработку информационных технологий и технических средств.
<b>2.6. Обезличивание персональных данных</b> – действия, в результате которых невозможно определить принадлежность персональных данных конкретному субъекту без использования дополнительной информации.
<b>2.7. Обработка персональных данных</b> – любое действие (операция) или совокупность действий с персональными данными, включая:
• сбор;
• запись;
• систематизацию;
• накопление;
• хранение;
• уточнение (обновление, изменение);
• извлечение;
• использование;
• передачу (в том числе трансграничную передачу);
• обезличивание;
• блокирование;
• удаление;
• уничтожение.
<b>2.8. Персональные данные</b> – личные сведения, относящиеся к определенному или определяемому субъекту персональных данных.
<b>2.9. Пользователь</b> – любой зарегистрированный пользователь Веб-сайта.

<b>3. Порядок применения настоящей Политики</b>
3.1. Политика о конфиденциальности подлежит применению в отношении персональных данных Пользователя, которые Компания собирает и/или обрабатывает и/или использует иным законным образом.
3.2. Используя Веб-сайт и предоставляя свои персональные данные, Пользователь подтверждает и соглашается с условиями настоящей Политики.

<b>4. Порядок связи Пользователя с Компанией</b>
4.1. По любым вопросам, касающимся персональных данных, Пользователь может обратиться к Компании простым письменным обращением по следующему адресу: А26T8F4, Республика Казахстан, г. Алматы, Медеуский район, ул. Рубинштейна, дом 48, или посредством электронной почты: <a href="mailto:Info.kz@pernod-ricard.com">Info.kz@pernod-ricard.com</a>.

<b>5. Цели сбора персональных данных Пользователя</b>
5.1. Персональные данные Пользователя собираются и обрабатываются Компанией в следующих законных целях:
5.1.1. Предоставление доступа к Веб-сайту и создание профиля пользователя;
5.1.2. Обеспечение доступа к функциям Веб-сайта (например, доступ к онлайн-контенту, участие в тренингах, регистрация на мероприятия), пользование Веб-сайтом и возможность принятия участия в активациях и мероприятиях, проводимых Компанией;
5.1.3. Информирование и приглашения на мероприятия, проводимые Компанией, а также предоставление информации о Компании и ее аффилированных лицах, о товарах и услугах Компании, об активностях Компании, о научных исследованиях или иных данных в отношении товаров или услуг Компании;
5.1.4. Приглашения к участию в активностях, опросах и исследованиях, программах, в том числе программах лояльности Компании или ее партнеров, включая исполнения обязательств Компании и/или партнеров по таким программам;
5.1.5. Пополнение CRM-базы путем добровольного предоставления персональных данных пользователями для участия в маркетинговых активностях, проводимых в отношении товарных знаков продукции;
5.1.6. Улучшения качества товаров и услуг, улучшения опыта взаимодействия потребителя с Компанией, разработки коммуникаций и сервисных решений;
5.1.7. Отправление административных уведомлений и сервисных сообщений;
5.1.8. Адаптация контента Пользователя Веб-сайта.

<b>6. Перечень собираемых и обрабатываемых персональных данных Пользователя</b>
6.1. В целях использования функционала Веб-сайта Пользователь предоставляет следующие персональные данные:
6.1.1. Фамилия, имя и отчество (при наличии) в соответствии с документом, удостоверяющим личность;
6.1.2. Дата рождения Пользователя, в целях подтверждение достижения им возраста 21 года;
6.1.3. Номер контактного мобильного телефона, зарегистрированного на имя Пользователя (11 цифр);
6.1.4. Город проживания;
6.1.5. Пол Пользователя;
6.1.6. Адрес электронной почты (e-mail) Пользователя.

<b>7. Обработка персональных данных Пользователя</b>
7.1. Обработка персональных данных будет осуществляться Компанией и третьими лицами (партнерами, операторами мобильной связи и пр.), действующими по поручению/заданию Компании, с соблюдением принципов и правил, предусмотренных Законом Республики Казахстан «О персональных данных и их защите» и иными применимыми нормативными актами (далее – Закон «О персональных данных»).
7.2. Под обработкой персональных данных понимается любое действие (операция) или совокупность действий (операций), совершаемых с использованием средств автоматизации или без использования таких средств с персональными данными, включая сбор, запись, систематизацию, накопление, хранение, уточнение (обновление, изменение), извлечение, использование, передачу (распространение, предоставление, доступ), трансграничную передачу, обезличивание, блокирование, удаление, уничтожение персональных данных Пользователя.

<b>8. Безопасность персональных данных Пользователя</b>
8.1. Компания, третьи лица (партнеры), действующие по поручению/заданию Компании, гарантируют необходимые меры защиты персональных данных от несанкционированного доступа. Все персональные данные, сообщенные Пользователем, будут храниться и обрабатываться Компанией, третьими лицами (партнерами), действующими по поручению/заданию Компании, в соответствии с действующим законодательством РК, иными местными и международными применимыми нормами, и с соблюдением гарантий, указанных в настоящей Политики.
8.2. Компания и иные партнеры, действующие по поручению/заданию Компании, обязуются соблюдать следующие правила и предоставляют Пользователю следующие гарантии в отношении обработки его персональных данных:
8.2.1. Обеспечить обработку персональных данных с соблюдением всех применимых требований законодательства РК в области защиты персональных данных, в том числе с соблюдением принципов, требований, обязательств оператора персональных данных, установленных Законом «О персональных данных»;
8.2.2. Обрабатывать персональные данные только в объеме и в целях, указанных в настоящей Политике, а также в целях дальнейшей коммуникации с Пользователем посредством электронной почты и в социальных сетях, мобильной связи. Использование и иные виды обработки персональных данных в целях информирования субъектов персональных данных о Продукции или услугах Компании, а также в любых иных целях будет осуществляться только в объеме и в случаях, предусмотренных Законом «О персональных данных»;
8.2.3. В случае если Компания в целях исполнения своих обязательств перед Пользователем должна передать или иным образом раскрыть персональные данные Пользователя третьим лицам, осуществлять указанные действия с соблюдением требований Закона «О персональных данных»;
8.2.4. Нести ответственность за охрану и обеспечение безопасности и конфиденциальности персональных данных Пользователя при их обработке в соответствии с требованиями законодательства РК;
8.2.5. Хранить и обрабатывать персональные данные Пользователя только в течение срока действия данного им Согласия на сбор и обработку персональных данных.

<b>9. Основные права и обязанности Компании</b>
9.1. Компания имеет право:
9.1.1. Получать от Пользователя достоверную информацию;
9.1.2. Самостоятельно определять необходимые меры защиты персональных данных.
9.2. Компания обязана:
9.2.1. Предоставлять Пользователю по его запросу полную и достоверную информацию об обработке его персональных данных;
9.2.2. Организовывать обработку персональных данных в соответствии с законодательством Республики Казахстан, иными локальными и международными применимыми нормами;
9.2.3. Принимать необходимые правовые, организационные и технические меры для защиты персональных данных;
9.2.4. Публиковать настоящую Политику или обеспечивать свободный доступ к ней;
9.2.5. Прекращать обработку и уничтожать персональные данные в случаях, предусмотренных законодательством Республики Казахстан.

<b>10. Права и обязанности Пользователя</b>
10.1. Пользователь имеет право:
10.1.1. Получать информацию о своих персональных данных и их обработке;
10.1.2. Требовать уточнения, блокирования или уничтожения персональных данных;
10.1.3. Отзывать согласие на обработку персональных данных;
10.1.4. Обжаловать действия Компании в уполномоченном государственном органе или в судебном порядке;
10.1.5. Пользователь может актуализировать свои персональные данные, направив запрос на электронную почту: <a href="mailto:Info.kz@pernod-ricard.com">Info.kz@pernod-ricard.com</a> с пометкой «Актуализация персональных данных».
10.2. Пользователь обязан:
10.2.1. Предоставлять достоверные данные о себе;
10.2.2. Своевременно сообщать об изменении своих персональных данных.

<b>11. Принципы обработки персональных данных Пользователя</b>
11.1. Обработка персональных данных осуществляется на основе следующих принципов:
11.1.1. Законности и справедливости;
11.1.2. Обработки только для конкретных и законных целей;
11.1.3. Недопущения избыточности персональных данных;
11.1.4. Обеспечения точности и актуальности персональных данных;
11.1.5. Хранения персональных данных не дольше, чем этого требуют цели обработки.

<b>12. Заключительные положения</b>
12.1. Пользователь может получить разъяснения по вопросам обработки персональных данных, направив запрос на: <a href="mailto:Info.kz@pernod-ricard.com">Info.kz@pernod-ricard.com</a>
12.2. Политика действует бессрочно. Компания вправе в любое время и по любому основанию вносить изменения в настоящую Политику.
12.3. Актуальная версия Политики размещается по адресу: <a href="https://jameson.prk-events.kz/teaser" target="_blank" rel="noopener">https://jameson.prk-events.kz/teaser</a>.`
        },
        offer: {
          title: "СОГЛАСИЕ НА СБОР И ОБРАБОТКУ ПЕРСОНАЛЬНЫХ ДАННЫХ",
          content: `Я подтверждаю, что достиг возраста 21 года и более и даю <b>ТОО «Перно Рикар Казахстан», (БИН 050840001045),</b> а также их работникам и третьим лицам-соисполнителям по заключенным между ними договорам, <b>свое согласие</b> на сбор, обработку, распространение и трансграничную передачу (без дополнительных уведомлений) моих персональных данных, относящихся исключительно к перечисленным ниже категориям:
1) ФИО
2) Город проживания
3) Мобильный телефон
4) E-mail
5) Пол

<b>Согласованная цель сбора и обработки персональных данных</b> – пополнение CRM базы посредством предоставления персональных данных любыми средствами связи для участия в активностях, проводимых в отношении товарных знаков продукции ТОО «Перно Рикар Казахстан».

Я даю согласие на использование персональных данных исключительно в вышеуказанных целях, а также на обработку персональных данных и их трансграничную передачу на всех электронных носителях, используемых для вышеуказанной цели.

Я предоставляю согласие на контактирование со мной для вышеуказанных целей путем телефонных звонков, направления мне уведомлений с использованием почты, SMS, мессенджеров, социальных сетей и электронной почты.

Данное согласие действует в течение срока хранения информации и может быть отозвано в любой момент по письменному заявлению на адрес promo.kz@pernod-ricard.com.`
        },
        rules: {
          title: "Полные правила активности",
          content: `<b>1. Общие положения</b>
1.1. Активация не является лотереей или азартной игрой, регулируемой Законом Республики Казахстан от 9 апреля 2016 года № 495-V «О лотереях и лотерейной деятельности».
1.2. Участие в активации является добровольным и означает полное согласие участника с настоящими Правилами.

<b>2. Условия участия</b>
2.1. К участию допускаются только физические лица, достигшие возраста 21 года на момент участия, постоянно проживающие на территории Республики Казахстан.
2.2. Для участия необходимо корректно заполнить анкету и подтвердить номер телефона через OTP код.

<b>3. Подведение итогов</b>
3.1. Все подробности и результаты участия будут направлены на указанный в анкете e-mail 1 ноября 2026 года.`
        }
      },

      formData: {
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        gender: "",
        city: "",
        consent: false
      },
      errors: {
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        gender: "",
        city: "",
        consent: ""
      },

      popularCities: [
        'Алматы', 'Астана', 'Шымкент', 'Актау', 'Актобе', 'Атырау',
        'Караганда', 'Жезказган', 'Кокшетау', 'Костанай', 'Кызылорда', 'Павлодар',
        'Петропавловск', 'Риддер', 'Рудный', 'Семей', 'Талдыкорган', 'Тараз',
        'Темиртау', 'Туркестан', 'Уральск', 'Усть-Каменогорск', 'Экибастуз'
      ],

      oblasts: [
        ['Абайская область', 'Восток', ['Аягоз', 'Курчатов']],
        ['Акмолинская область', 'Север', ['Косшы', 'Степногорск', 'Щучинск']],
        ['Актюбинская область', 'Запад', ['Кандыагаш', 'Хромтау']],
        ['Алматинская область', 'Алматы', ['Конаев', 'Есик', 'Каскелен', 'Талгар']],
        ['Атырауская область', 'Запад', ['Кульсары']],
        ['Восточно-Казахстанская область', 'Восток', ['Алтай']],
        ['Жамбылская область', 'Юг', ['Каратау', 'Шу']],
        ['Жетысуская область', 'Алматы', ['Текели', 'Ушарал']],
        ['Западно-Казахстанская область', 'Запад', ['Аксай']],
        ['Карагандинская область', 'Восток', ['Балхаш', 'Сарань', 'Шахтинск']],
        ['Костанайская область', 'Север', ['Аркалык', 'Житикара']],
        ['Кызылординская область', 'Юг', ['Аральск', 'Байконур']],
        ['Мангистауская область', 'Запад', ['Жанаозен']],
        ['Павлодарская область', 'Восток', ['Аксу']],
        ['Северо-Казахстанская область', 'Север', ['Тайынша']],
        ['Туркестанская область', 'Юг', ['Арыс', 'Кентау', 'Сарыагаш']],
        ['Улытауская область', 'Восток', ['Каражал', 'Сатпаев']]
      ],

      i18n: {
        title: ['Jameson — в Ирландию в крутой компании', 'Jameson — Ирландияға бірге кеттік!'],
        skip: ['Перейти к анкете', 'Анкетаға өту'],
        lang_aria: ['Язык', 'Тіл'],
        h1: ['Хочешь в Ирландию в крутой компании?', 'Мықты компаниямен Ирландияға кеттік пе?'],
        lead: ['Заполняй анкету.', 'Анкетаны толтыр — қалғанын бізге қалдыр.'],
        cta_fill: ['Заполнить анкету', 'Анкетаны толтыру'],
        cta_more: ['Узнать подробнее', 'Қалай қатысамын?'],
        cue: ['Как участвовать', 'Қалай қатысамын'],
        kv_aria: ['Знак Jameson — фирменная буква J', 'Jameson белгісі — фирмалық J әрпі'],
        how_title: ['Как участвовать?', 'Қалай қатысасың?'],
        how_sub: ['Два шага. Остальное мы расскажем сами.', 'Бар болғаны екі қадам — қалғанын өзіміз айтамыз.'],
        step1: ['Шаг 1', '1-ші қадам'],
        step2: ['Шаг 2', '2-ші қадам'],
        s1_t: ['Заполни анкету', 'Анкетаны толтыр'],
        s1_p: ['Простые данные, чтобы мы потом смогли тебе сообщить больше подробностей.', 'Бірнеше сұрақ, бір минут — кейін бәрін айтып береміз.'],
        s2_t: ['Жди результаты', 'Хатымызды күт'],
        s2_p: ['Мы отправим письмо на твою почту со всеми подробностями.', 'Барлық мәліметті поштаңа жібереміз — «Спам» папкасын да қарап қой.'],
        date: ['1 ноября 2026', 'Старт — 2026 жылғы 1 қараша'],
        apply_title: ['Анкета', 'Анкета'],
        apply_sub: ['Займёт минуту. Все подробности придут на почту 1&nbsp;ноября 2026&nbsp;года.', 'Бір минут қана. Ал 2026&nbsp;жылғы 1&nbsp;қарашада бәрін поштаңа жібереміз.'],
        st_idle: ['Займёт минуту', 'Бір минут қана'],
        st_sending: ['Отправляем…', 'Жіберіп жатырмыз…'],
        st_sent: ['Анкета отправлена', 'Анкета кетті!'],
        st_err: ['Ошибка отправки', 'Бірдеңе дұрыс болмады'],
        progress: ['{n} из 7', '{n} / 7'],
        form_aria: ['Анкета', 'Анкета'],
        l_first: ['Имя', 'Аты'],
        l_last: ['Фамилия', 'Тегі'],
        l_phone: ['Номер телефона', 'Телефон'],
        l_email: ['Email', 'E-mail'],
        l_gender: ['Пол', 'Жыныс'],
        g_m: ['Мужской', 'Жігіт'],
        g_f: ['Женский', 'Қыз'],
        l_city: ['Город', 'Қала'],
        city_ph: ['Выбери город', 'Қалаңды таңда'],
        popular: ['Популярные города', 'Танымал қалалар'],
        consent: [
          'Я подтверждаю, что мне исполнился 21 год, и даю согласие на сбор и обработку персональных данных в соответствии с <a href="#" class="policy-link" data-popup="policy">политикой конфиденциальности</a>.',
          'Маған 21 жас толғанын растаймын және <a href="#" class="policy-link" data-popup="policy">құпиялылық саясатына</a> сәйкес дербес деректерімді жинауға және өңдеуге келісім беремін.'
        ],
        send: ['Отправить анкету', 'Анкетаны жіберу'],
        sending: ['Отправляем анкету…', 'Жіберіп жатырмыз…'],
        send_again: ['Отправить ещё раз', 'Тағы бір рет жіберу'],
        e_first: ['Имя должно быть не короче двух букв.', 'Атың кемінде екі әріптен тұрсын.'],
        e_last: ['Фамилия должна быть не короче двух букв.', 'Тегің кемінде екі әріптен тұрсын.'],
        e_email: ['Проверь адрес: нужны @ и домен, например name@mail.kz.', 'Поштаңды тексерші: @ пен домен керек, мысалы name@mail.kz.'],
        e_phone: ['Нужно 10 цифр после +7, например +7 (701) 123-45-67.', '+7-ден кейін 10 цифр керек, мысалы +7 (701) 123-45-67.'],
        e_gender: ['Выбери один из вариантов.', 'Біреуін таңдашы.'],
        e_city: ['Выбери город из списка.', 'Тізімнен қалаңды таңдашы.'],
        e_consent: ['Без согласия мы не сможем сохранить анкету и написать тебе.', 'Келісімсіз анкетаңды сақтай алмаймыз — құсбелгі қойшы.'],
        x_net: ['Не получилось отправить анкету', 'Ой, жіберілмей қалды'],
        x_net_t: ['Проверьте подключение к интернету или попробуйте отправить ещё раз.', 'Интернет байланысын тексеріп, қайта көріңіз.'],
        done_title: ['Анкета отправлена', 'Анкетаң бізде!'],
        done_text: ['1 ноября 2026 года пришлём письмо на {email} со всеми подробностями.', '2026 жылғы 1 қарашада {email} поштасына бәрін жібереміз. Күте тұр!'],
        done_region: ['Твой регион — {r}', 'Сенің өңірің — {r}'],
        close: ['Закрыть', 'Жабу'],
        ok: ['Закрыть', 'Түсінікті'],
        resp: ['Пожалуйста, употребляйте Jameson ответственно', 'Jameson-ды жауапкершілікпен тұтыныңыз'],
        legal_aria: ['Правовая информация', 'Құқықтық ақпарат'],
        pp: ['Политика конфиденциальности', 'Құпиялылық саясаты'],
        terms: ['Условия использования', 'Пайдалану шарттары'],
        resp_link: ['Ответственное потребление', 'Жауапты тұтыну'],
        ig: ['Jameson Kazakhstan в Instagram', 'Jameson Kazakhstan Instagram-да'],
        legal: ['Ссылкой на этот ресурс запрещается делиться с лицами младше 21 года.', 'Бұл ресурстың сілтемесін 21 жасқа толмаған адамдармен бөлісуге тыйым салынады.']
      },

      regionKz: {
        'Алматы': 'Алматы', 'Астана': 'Астана', 'Север': 'Солтүстік',
        'Восток': 'Шығыс', 'Запад': 'Батыс', 'Юг': 'Оңтүстік'
      },
      oblastKz: {
        'Абайская область': 'Абай облысы', 'Акмолинская область': 'Ақмола облысы',
        'Актюбинская область': 'Ақтөбе облысы', 'Алматинская область': 'Алматы облысы',
        'Атырауская область': 'Атырау облысы', 'Восточно-Казахстанская область': 'Шығыс Қазақстан облысы',
        'Жамбылская область': 'Жамбыл облысы', 'Жетысуская область': 'Жетісу облысы',
        'Западно-Казахстанская область': 'Батыс Қазақстан облысы', 'Карагандинская область': 'Қарағанды облысы',
        'Костанайская область': 'Қостанай облысы', 'Кызылординская область': 'Қызылорда облысы',
        'Мангистауская область': 'Маңғыстау облысы', 'Павлодарская область': 'Павлодар облысы',
        'Северо-Казахстанская область': 'Солтүстік Қазақстан облысы', 'Туркестанская область': 'Түркістан облысы',
        'Улытауская область': 'Ұлытау облысы'
      },
      cityKz: {
        'Актау': 'Ақтау', 'Актобе': 'Ақтөбе', 'Караганда': 'Қарағанды', 'Жезказган': 'Жезқазған',
        'Кокшетау': 'Көкшетау', 'Костанай': 'Қостанай', 'Кызылорда': 'Қызылорда', 'Петропавловск': 'Петропавл',
        'Талдыкорган': 'Талдықорған', 'Темиртау': 'Теміртау', 'Туркестан': 'Түркістан', 'Уральск': 'Орал',
        'Усть-Каменогорск': 'Өскемен', 'Экибастуз': 'Екібастұз', 'Аягоз': 'Аягөз', 'Косшы': 'Қосшы',
        'Степногорск': 'Степногор', 'Кандыагаш': 'Қандыағаш', 'Конаев': 'Қонаев', 'Есик': 'Есік',
        'Каскелен': 'Қаскелең', 'Талгар': 'Талғар', 'Кульсары': 'Құлсары', 'Каратау': 'Қаратау',
        'Текели': 'Текелі', 'Ушарал': 'Үшарал', 'Аксай': 'Ақсай', 'Балхаш': 'Балқаш', 'Сарань': 'Саран',
        'Аркалык': 'Арқалық', 'Житикара': 'Жітіқара', 'Аральск': 'Арал', 'Байконур': 'Байқоңыр',
        'Жанаозен': 'Жаңаөзен', 'Аксу': 'Ақсу', 'Сарыагаш': 'Сарыағаш', 'Каражал': 'Қаражал', 'Сатпаев': 'Сәтбаев'
      },

      // Parallax state
      rafId: null,
      introStartTime: 0,
      targetX: 0,
      targetY: 0,
      currX: 0,
      currY: 0,
      targetScroll: 0,
      currScroll: 0,
      stageWidth: 0,
      stageHeight: 0,
      isParallaxVisible: true,
      observer: null
    };
  },
  created() {
    if (localStorage.getItem("userAge") !== null) {
      this.showConfirm = false;
    }
    const savedLang = localStorage.getItem("jameson_lang");
    const qLang = (this.$route.query.lang || "").toLowerCase();
    if (qLang === "kz" || qLang === "kk" || (!this.$route.query.lang && savedLang === "kz")) {
      this.currentLang = "kz";
    }
  },
  mounted() {
    initLanding({
      brand: "jameson",
      activation: "jameson_triple_trip",
      landing_id: "jameson-triple-trip",
      landing_type: "registration"
    });
    this.getFormFields();

    // Global JavaScript Error tracking (js_error)
    this.onJsError = (event) => {
      trackJsError({
        errorMessage: event.message || "Unknown JS Error",
        errorSource: event.filename ? event.filename.split("/").pop() : "inline",
        errorLine: event.lineno || 0,
        errorCol: event.colno || 0,
        brand: "jameson"
      });
    };
    this.onUnhandledRejection = (event) => {
      const reason = event.reason;
      trackJsError({
        errorMessage: reason ? String(reason.message || reason) : "Unhandled Promise Rejection",
        errorSource: "unhandled_promise",
        brand: "jameson"
      });
    };
    window.addEventListener("error", this.onJsError);
    window.addEventListener("unhandledrejection", this.onUnhandledRejection);

    if (!this.showConfirm) {
      this.$nextTick(() => {
        this.startParallaxAndReveals();
      });
    }
  },
  errorCaptured(err, vm, info) {
    trackJsError({
      errorMessage: err ? String(err.message || err) : "Vue error",
      errorSource: `vue_${info || "unknown"}`,
      brand: "jameson"
    });
    return false;
  },
  watch: {
    getUserAge(newVal) {
      if (!newVal) return;
      if (this.showConfirm) {
        this.onConfirmed();
      }
      if (this.formSchema?.questions) {
        const birthQuestion = this.formSchema.questions.find(q => q.type === 3);
        if (birthQuestion) {
          this.$set(this.formData, birthQuestion.id, newVal);
        }
      }
    }
  },
  beforeDestroy() {
    if (this.smsTimerInterval) {
      clearInterval(this.smsTimerInterval);
    }
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    window.removeEventListener("scroll", this.onWindowScroll);
    window.removeEventListener("resize", this.onWindowResize);
    window.removeEventListener("pointermove", this.onPointerMove);
    document.documentElement.removeEventListener("pointerleave", this.onPointerLeave);
    if (this.onJsError) window.removeEventListener("error", this.onJsError);
    if (this.onUnhandledRejection) window.removeEventListener("unhandledrejection", this.onUnhandledRejection);
    document.body.style.overflow = "";
  },
  computed: {
    ...mapGetters(["getUserAge"]),
    statusMessage() {
      if (this.isSending || this.isSendSms) return this.t("st_sending");
      if (this.hasSubmittedOnce && !this.globalAlert) return this.t("st_sent");
      if (this.globalAlert) return this.t("st_err");
      return this.t("st_idle");
    },
    filledCount() {
      let count = 0;
      if (this.formData.first_name && this.formData.first_name.trim().length >= 2) count++;
      if (this.formData.last_name && this.formData.last_name.trim().length >= 2) count++;
      if (this.formData.email && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(this.formData.email.trim())) count++;
      const digits = (this.formData.phone || "").replace(/\D/g, "");
      if (digits.length === 11) count++;
      if (this.formData.gender) count++;
      if (this.formData.city) count++;
      if (this.formData.consent === true) count++;
      return count;
    },
    progressPercent() {
      return Math.min(100, Math.round((this.filledCount / 7) * 100));
    },
    progressText() {
      return this.t("progress").replace("{n}", this.filledCount);
    },
    doneEmailText() {
      const email = this.submittedData?.email || this.formData.email || "";
      return this.t("done_text").replace("{email}", email);
    },
    doneRegionText() {
      const city = this.submittedData?.city || this.formData.city;
      const reg = this.regionOf(city);
      if (!reg) return "";
      return this.t("done_region").replace("{r}", this.regionLabel(reg));
    },
    currentPopupData() {
      if (!this.currentPopup || !this.popupTexts) return null;
      return this.popupTexts[this.currentPopup] || null;
    }
  },
  methods: {
    t(key) {
      const v = this.i18n[key];
      if (!v) return key;
      return this.currentLang === "kz" ? v[1] : v[0];
    },
    setLang(l) {
      if (this.currentLang === l) return;
      const prev = this.currentLang;
      this.currentLang = l;
      localStorage.setItem("jameson_lang", l);
      trackEvent("language_change", {
        previous_language: prev,
        language: l,
        brand: "jameson"
      });
      // Re-validate fields to update error language if any are visible
      Object.keys(this.errors).forEach(k => {
        if (this.errors[k]) {
          this.validateField(k);
        }
      });
    },
    cityLabel(c) {
      if (!c) return "";
      if (this.crmCities && this.crmCities.length > 0) {
        const found = this.crmCities.find(item => item.id === c || item.name === c);
        if (found && found.name) c = found.name;
      }
      return this.currentLang === "kz" ? this.cityKz[c] || c : c;
    },
    oblastLabel(o) {
      return this.currentLang === "kz" ? this.oblastKz[o] || o : o;
    },
    regionLabel(r) {
      return this.currentLang === "kz" ? this.regionKz[r] || r : r;
    },
    regionOf(c) {
      if (!c) return null;
      if (this.crmCities && this.crmCities.length > 0) {
        const found = this.crmCities.find(item => item.id === c || item.name === c);
        if (found && found.name) c = found.name;
      }
      if (c === "Косшы") return "Астана";
      const popMap = {
        'Алматы': 'Алматы', 'Астана': 'Астана', 'Шымкент': 'Юг', 'Актау': 'Запад',
        'Актобе': 'Запад', 'Атырау': 'Запад', 'Караганда': 'Восток', 'Жезказган': 'Восток',
        'Кокшетау': 'Север', 'Костанай': 'Север', 'Кызылорда': 'Юг', 'Павлодар': 'Восток',
        'Петропавловск': 'Север', 'Риддер': 'Восток', 'Рудный': 'Север', 'Семей': 'Восток',
        'Талдыкорган': 'Алматы', 'Тараз': 'Юг', 'Темиртау': 'Восток', 'Туркестан': 'Юг',
        'Уральск': 'Запад', 'Усть-Каменогорск': 'Восток', 'Экибастуз': 'Восток'
      };
      if (popMap[c]) return popMap[c];
      for (let o of this.oblasts) {
        if (o[2].includes(c)) return o[1];
      }
      return null;
    },
    async getFormFields() {
      this.isLoadingForm = true;
      this.formError = false;
      try {
        const res = await this.$http.get("/api/survey", {
          params: {
            campaignId: this.campaignId
          }
        });
        this.formSchema = res.data;
        if (res.data?.questions) {
          const cityQ = res.data.questions.find(q =>
            q.type === 5 ||
            (q.name && (q.name.toLowerCase().includes("город") || q.name.toLowerCase().includes("қала")))
          );
          if (cityQ?.answers?.length) {
            this.crmCities = cityQ.answers;
          }
        }
      } catch (err) {
        this.formError = true;
        console.error("Ошибка загрузки формы CRM", err);
        trackFormSubmitError({
          errorType: "crm_load_error",
          errorMessage: err?.message || "CRM survey fetch failed",
          step: "get_form_fields",
          brand: "jameson"
        });
      } finally {
        this.isLoadingForm = false;
      }
    },
    onConfirmed() {
      this.showConfirm = false;
      this.$nextTick(() => {
        this.startParallaxAndReveals();
      });
    },
    scrollToApply(location = "hero") {
      trackCtaClick({
        location,
        ctaName: "fill_form",
        brand: "jameson",
        lang: this.currentLang
      });
      const el = document.getElementById("apply");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    scrollToHow(location = "hero") {
      trackCtaClick({
        location,
        ctaName: "learn_more",
        brand: "jameson",
        lang: this.currentLang
      });
      const el = document.getElementById("how");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    onFieldFocus() {
      if (!this.formStarted) {
        this.formStarted = true;
        trackFormStart({ formId: "teaser_registration", formMode: "классика" });
      }
    },
    trackStepIfCompleted(stepName) {
      if (!this.completedSteps) {
        this.completedSteps = [];
      }
      if (!this.completedSteps.includes(stepName)) {
        this.completedSteps.push(stepName);
        trackFormStepComplete({
          stepName,
          formId: "teaser_registration",
          brand: "jameson"
        });
      }
    },
    onPhoneFocus() {
      this.onFieldFocus("phone");
      if (!this.formData.phone) {
        this.formData.phone = "+7";
      }
    },
    onPhoneInput() {
      const val = this.formData.phone || "";
      if (!val.startsWith("+7")) {
        this.formData.phone = "+7" + val.replace(/\D/g, "").slice(1);
      }
      this.validateField("phone");
    },
    validateField(field) {
      let err = "";
      switch (field) {
        case "first_name":
          if (!this.formData.first_name || this.formData.first_name.trim().length < 2) {
            err = this.t("e_first");
          } else if (!/^[А-Яа-яA-Za-z\u0400-\u04FF\s-]+$/.test(this.formData.first_name.trim())) {
            err = "Имя должно содержать только буквы.";
          }
          break;
        case "last_name":
          if (!this.formData.last_name || this.formData.last_name.trim().length < 2) {
            err = this.t("e_last");
          } else if (!/^[А-Яа-яA-Za-z\u0400-\u04FF\s-]+$/.test(this.formData.last_name.trim())) {
            err = "Фамилия должна содержать только буквы.";
          }
          break;
        case "email":
          if (!this.formData.email || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(this.formData.email.trim())) {
            err = this.t("e_email");
          }
          break;
        case "phone": {
          const digits = (this.formData.phone || "").replace(/\D/g, "");
          if (digits.length < 11) {
            err = this.t("e_phone");
          }
          break;
        }
        case "gender":
          if (!this.formData.gender) {
            err = this.t("e_gender");
          }
          break;
        case "city":
          if (!this.formData.city) {
            err = this.t("e_city");
          }
          break;
        case "consent":
          if (!this.formData.consent) {
            err = this.t("e_consent");
          }
          break;
      }
      this.$set(this.errors, field, err);
      if (!err) {
        this.trackStepIfCompleted(field);
      }
      return !err;
    },
    validateAll() {
      let isValid = true;
      const failedFields = [];
      ["first_name", "last_name", "email", "phone", "gender", "city", "consent"].forEach(k => {
        const ok = this.validateField(k);
        if (!ok) {
          isValid = false;
          failedFields.push(k);
          let code = "validation_error";
          if (k === "consent") code = "consent_required";
          else if (k === "phone") code = "phone_invalid_format";
          else if (k === "email") code = "email_invalid_format";
          else if (k === "first_name" || k === "last_name") code = `${k}_invalid_format`;
          else code = `${k}_empty`;

          trackFormError({
            formId: "teaser_registration",
            errorType: "validation",
            fieldName: k,
            errorTextCode: code
          });
        }
      });
      if (!isValid) {
        trackFormSubmitError({
          errorType: "validation",
          errorMessage: `Поля не прошли валидацию: ${failedFields.join(", ")}`,
          step: "validation",
          brand: "jameson"
        });
      }
      return isValid;
    },
    async submitForm() {
      if (this.isSending || this.isSendSms) return;
      this.hasSubmittedOnce = true;
      this.globalAlert = null;

      if (!this.validateAll()) {
        const firstErr = document.querySelector(".cf.bad input, .cf.bad select");
        if (firstErr) firstErr.focus();
        this.snackbarMessage = "Пожалуйста, заполните все обязательные поля корректно";
        this.snackbar = true;
        return;
      }

      trackFormSubmit("teaser_registration");

      try {
        const isSent = await this.sendVerificationCode();
        if (isSent && !this.otpErrorSms) {
          this.otpError = "";
          this.otpSuccess = false;
          this.showOtpPopup = true;
          this.startSmsTimer();
        }
      } catch (err) {
        this.snackbarMessage = "Ошибка при отправке кода подтверждения";
        this.snackbar = true;
        trackFormSubmitError({
          errorType: "sms_send_exception",
          errorMessage: err?.message || "Submit form sms exception",
          step: "submit_form",
          brand: "jameson"
        });
      }
    },
    startSmsTimer() {
      if (this.smsTimerInterval) clearInterval(this.smsTimerInterval);
      this.smsTimer = 60;
      this.smsTimerInterval = setInterval(() => {
        if (this.smsTimer > 0) {
          this.smsTimer--;
        } else {
          clearInterval(this.smsTimerInterval);
          this.smsTimerInterval = null;
        }
      }, 1000);
    },
    async resendSms() {
      this.startSmsTimer();
      await this.sendVerificationCode();
    },
    async sendVerificationCode() {
      const phoneQuestion = this.formSchema?.questions?.find(q => q.type === 4);
      const cleanPhone = (this.formData.phone || "").replace(/[^\d+]/g, "");
      if (!cleanPhone) return false;

      try {
        this.isSendSms = true;
        this.isSending = true;

        let recaptchaToken = "";
        if (this.$recaptchaLoaded && this.$recaptcha) {
          try {
            await this.$recaptchaLoaded();
            recaptchaToken = await this.$recaptcha("send_sms");
          } catch (rErr) {
            console.warn("reCAPTCHA loader:", rErr);
          }
        }

        const answerDto = {
          campaignId: this.campaignId,
          surveyAnswer: {
            surveyQuestionId: phoneQuestion ? phoneQuestion.id : "922eafb5-fa99-f011-b7ff-00155d79212a",
            answer: cleanPhone
          },
          recaptchaToken: recaptchaToken
        };

        await this.$http.post("/api/Verification/SendCode", answerDto);
        this.otpErrorSms = false;
        this.trackStepIfCompleted("sms_code_sent");
        return true;
      } catch (err) {
        console.error("Ошибка при отправке кода:", err);
        const errMsg = err?.response?.data?.message || err?.message || "Ошибка при отправке кода";
        trackFormSubmitError({
          errorType: err?.response?.status === 409 ? "phone_conflict" : err?.response?.status === 403 ? "recaptcha_blocked" : "sms_api_error",
          errorMessage: errMsg,
          step: "send_verification_code",
          brand: "jameson"
        });
        if (err?.response?.status === 409) {
          this.otpErrorSms = true;
          this.showOtpPopup = true;
          this.otpSuccess = false;
          return false;
        }
        if (err?.response?.status === 403) {
          this.snackbarMessage = "Подозрение на бота. Попробуйте перезагрузить страницу.";
          this.snackbar = true;
          return false;
        }
        this.snackbarMessage = err?.response?.data?.message || "Ошибка при отправке кода. Попробуйте снова.";
        this.snackbar = true;
        return false;
      } finally {
        this.isSendSms = false;
        this.isSending = false;
      }
    },
    async submitAnswersWithCode(code) {
      if (this.otpVerifying || this.isRegistrationCompleted) return;
      this.otpVerifying = true;
      this.otpError = "";
      this.otpSuccess = false;

      const cleanPhone = (this.formData.phone || "").replace(/[^\d+]/g, "");
      const answers = [];

      const schemaQuestions = this.formSchema?.questions || [
        { id: "32a87c86-fa99-f011-b7ff-00155d79212a", type: 1, name: "Имя" },
        { id: "9c987091-fa99-f011-b7ff-00155d79212a", type: 2, name: "Фамилия" },
        { id: "9be1c6fc-9f9a-f011-b7ff-00155d79212a", type: 6, name: "Пол" },
        { id: "9d8888a2-fa99-f011-b7ff-00155d79212a", type: 7, name: "E-mail" },
        { id: "922eafb5-fa99-f011-b7ff-00155d79212a", type: 4, name: "Телефон" },
        { id: "02718dc1-fa99-f011-b7ff-00155d79212a", type: 5, name: "Город" },
        { id: "c1edc9d2-fa99-f011-b7ff-00155d79212a", type: 9, name: "Какой Jameson ты предпочитаешь?" },
        { id: "f815a28e-fb99-f011-b7ff-00155d79212a", type: 11, name: "Я даю согласие на сбор и обработку персональных данных" },
        { id: "c832df9c-fb99-f011-b7ff-00155d79212a", type: 12, name: "Я согласен на получение рекламных и информационных рассылок" },
        { id: "e16e6e92-259d-f011-b7ff-00155d79212a", type: 3, name: "Дата рождения" }
      ];

      schemaQuestions.forEach(q => {
        let ans = null;
        switch (q.type) {
          case 1:
            ans = this.formData.first_name.trim();
            break;
          case 2:
            ans = this.formData.last_name.trim();
            break;
          case 3:
            ans = this.getUserAge || localStorage.getItem("userAge") || "2000-01-01";
            break;
          case 4:
            ans = cleanPhone;
            break;
          case 5: {
            const match = (q.answers || []).find(a =>
              a.id === this.formData.city ||
              a.name?.trim().toLowerCase() === this.formData.city?.trim().toLowerCase()
            );
            ans = match ? match.id : this.formData.city;
            break;
          }
          case 6: {
            if (this.formData.gender === "Мужской" || this.formData.gender === "1") {
              ans = "1";
            } else if (this.formData.gender === "Женский" || this.formData.gender === "2") {
              ans = "2";
            } else {
              ans = this.formData.gender;
            }
            break;
          }
          case 7:
            ans = this.formData.email.trim();
            break;
          case 9:
            ans = (q.answers && q.answers.length > 0) ? q.answers[0].id : "14c06d07-fb3a-f011-b7f2-00155d799019";
            break;
          case 11:
          case 12:
            ans = this.formData.consent ? "True" : "False";
            break;
          default:
            break;
        }

        if (ans !== null && ans !== undefined) {
          answers.push({
            surveyQuestionId: q.id,
            answer: String(ans)
          });
        }
      });

      const payload = {
        surveyId: this.formSchema?.id || "32473630-fa99-f011-b7ff-00155d79212a",
        campaignId: this.campaignId,
        verificationCode: code,
        answers: answers
      };

      try {
        await this.$http.post("/api/AnswerSet", payload);
        this.trackStepIfCompleted("otp_verified");
        this.handleSuccess();
      } catch (error) {
        console.error("AnswerSet error:", error);
        const msg = error?.response?.data?.message || "Код введён неверно";
        this.otpError = msg;
        trackFormError({
          formId: "teaser_registration",
          errorType: "server",
          fieldName: "otp_code",
          errorTextCode: "otp_invalid_code"
        });
        trackFormSubmitError({
          errorType: "otp_verification_failed",
          errorMessage: msg,
          step: "submit_answers_with_code",
          brand: "jameson"
        });
      } finally {
        this.otpVerifying = false;
      }
    },
    handleSuccess() {
      if (this.isRegistrationCompleted) return;
      // Сохраняем отправленные данные для подстановки в итоговое модальное окно (email, город/регион)
      this.submittedData = {
        first_name: this.formData.first_name,
        last_name: this.formData.last_name,
        email: this.formData.email,
        phone: this.formData.phone,
        city: this.formData.city,
        gender: this.formData.gender,
        consent: this.formData.consent
      };
      this.isRegistrationCompleted = true;
      this.hasSubmittedOnce = true;

      const userRegion = this.regionOf(this.formData.city) || "Алматы";

      // Главная конверсия с распределением по 6 регионам
      trackGenerateLead({
        region: userRegion,
        city: this.formData.city,
        brand: "jameson",
        lang: this.currentLang
      });

      trackRegistrationSuccess({
        formId: "teaser_registration",
        city: this.formData.city,
        productChoice: "Jameson Original"
      });
      trackVirtualPageView("/jameson/thank-you", "Анкета отправлена");

      // 1. Показываем в OTP попапе успешное состояние
      this.otpSuccess = true;

      // 2. Спустя 800мс плавно закрываем OTP попап
      setTimeout(() => {
        this.showOtpPopup = false;

        // 3. После того как анимация закрытия OTP полностью завершится (400мс),
        // открываем итоговое окно тизера Jameson без наложений и конфликтов
        setTimeout(() => {
          this.otpSuccess = false;
          this.showDoneModal = true;
          document.body.style.overflow = "hidden";
          this.resetForm();
        }, 400);
      }, 800);
    },
    handleOtpClose() {
      this.showOtpPopup = false;
      this.otpError = "";
      if (this.isRegistrationCompleted) {
        setTimeout(() => {
          this.otpSuccess = false;
          this.showDoneModal = true;
          document.body.style.overflow = "hidden";
        }, 350);
      }
    },
    resetForm() {
      this.formData = {
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        gender: "",
        city: "",
        consent: false
      };
      this.errors = {
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        gender: "",
        city: "",
        consent: ""
      };
      this.hasSubmittedOnce = false;
    },
    closeDoneModal() {
      this.showDoneModal = false;
      this.isRegistrationCompleted = false;
      document.body.style.overflow = "";
      const applySection = document.getElementById("applyTitle");
      if (applySection) {
        applySection.setAttribute("tabindex", "-1");
        applySection.focus({ preventScroll: true });
      }
    },
    openPopup(type) {
      this.currentPopup = type;
      this.isLegalPopupOpen = true;
      document.body.style.overflow = "hidden";
    },
    closeLegalPopup() {
      this.isLegalPopupOpen = false;
      this.currentPopup = null;
      if (!this.showDoneModal) {
        document.body.style.overflow = "";
      }
    },
    handleConsentClick(e) {
      const link = e.target.closest("a");
      if (link) {
        e.preventDefault();
        e.stopPropagation();
        const popupType = link.getAttribute("data-popup") || "offer";
        this.openPopup(popupType);
      }
    },

    // Parallax & Reveals
    startParallaxAndReveals() {
      this.initParallax();
      this.initReveals();
    },
    initParallax() {
      const stage = this.$refs.kvStage;
      const wrap = this.$refs.kvLayers;
      if (!stage || !wrap) return;

      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduce) {
        this.isIntro = false;
        return;
      }

      this.isIntro = true;
      const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
      const touch = !fine;
      const MX = 0.024, MY = 0.018;
      const SCROLL = touch ? 0.09 : 0.04;
      const INTRO_Y = touch ? 0.08 : 0.05;
      const INTRO_MS = touch ? 1500 : 1300;

      const layers = Array.from(wrap.querySelectorAll("[data-depth]")).map(el => ({
        el,
        d: parseFloat(el.dataset.depth) || 0
      }));

      const size = () => {
        const r = stage.getBoundingClientRect();
        this.stageWidth = r.width;
        this.stageHeight = r.height;
      };

      const ease = t => 1 - Math.pow(1 - t, 3);

      const frame = now => {
        this.rafId = 0;
        this.currX += (this.targetX - this.currX) * 0.08;
        this.currY += (this.targetY - this.currY) * 0.08;
        this.currScroll += (this.targetScroll - this.currScroll) * 0.18;

        const ip = this.introStartTime
          ? Math.min(1, (now - this.introStartTime) / INTRO_MS)
          : 1;

        const settling =
          Math.abs(this.targetX - this.currX) > 0.0005 ||
          Math.abs(this.targetY - this.currY) > 0.0005 ||
          Math.abs(this.targetScroll - this.currScroll) > 0.0005 ||
          ip < 1;

        for (let L of layers) {
          const d = L.d;
          if (!d) continue;
          const lag = Math.max(0, Math.min(1, ip * 1.25 - d * 0.25));
          const x = this.currX * MX * this.stageWidth * d;
          const y =
            this.currY * MY * this.stageHeight * d -
            this.currScroll * SCROLL * this.stageHeight * d +
            (1 - ease(lag)) * INTRO_Y * this.stageHeight * d;

          L.el.style.transform = `translate3d(${x.toFixed(2)}px,${y.toFixed(2)}px,0)`;
        }

        if (settling && this.isParallaxVisible) {
          this.rafId = requestAnimationFrame(frame);
        }
      };

      const kick = () => {
        if (!this.rafId && this.isParallaxVisible) {
          this.rafId = requestAnimationFrame(frame);
        }
      };

      this.onWindowScroll = () => {
        const r = stage.getBoundingClientRect();
        this.targetScroll = Math.max(0, Math.min(1, -r.top / Math.max(1, r.height)));
        kick();
      };

      this.onPointerMove = e => {
        if (!fine) return;
        this.targetX = (e.clientX / window.innerWidth - 0.5) * 2;
        this.targetY = (e.clientY / window.innerHeight - 0.5) * 2;
        kick();
      };

      this.onPointerLeave = () => {
        this.targetX = 0;
        this.targetY = 0;
        kick();
      };

      this.onWindowResize = () => {
        size();
        kick();
      };

      size();
      this.onWindowScroll();

      window.addEventListener("resize", this.onWindowResize, { passive: true });
      window.addEventListener("scroll", this.onWindowScroll, { passive: true });
      window.addEventListener("pointermove", this.onPointerMove, { passive: true });
      document.documentElement.addEventListener("pointerleave", this.onPointerLeave);

      if ("IntersectionObserver" in window) {
        this.observer = new IntersectionObserver(es => {
          this.isParallaxVisible = es[0].isIntersecting;
          if (this.isParallaxVisible) kick();
        });
        this.observer.observe(stage);
      }

      setTimeout(() => {
        this.isIntro = false;
        this.introStartTime = performance.now();
        size();
        kick();
      }, 80);
    },
    initReveals() {
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduce || !("IntersectionObserver" in window)) {
        this.isAnim = false;
        return;
      }

      this.isAnim = true;
      this.$nextTick(() => {
        const narrow = window.matchMedia("(max-width: 759px)").matches;
        const mark = (sel, cls, delay) => {
          document.querySelectorAll(sel).forEach((el, i) => {
            el.classList.add(cls);
            const d = typeof delay === "function" ? delay(i) : delay;
            el.style.setProperty("--d", d + "ms");
          });
        };

        mark(".hero .tt-title", "rv", 100);
        mark(".hero .h1-text", "rv", 230);
        mark(".hero .lead", "rv", 340);
        mark(".hero .actions", "rv", 450);
        mark(".scroll-cue", "rv", 560);
        mark(".how .shead h2", "rv-clip", 0);
        mark(".how .shead p", "rv", 140);
        mark(".step", "rv", i => (narrow ? 0 : i * 160));
        mark(".step .ico", "rv-pop", i => (narrow ? 0 : i * 160) + 220);
        mark(".step .date", "rv-date", narrow ? 250 : 410);
        mark(".apply .shead h2", "rv-clip", 0);
        mark(".apply .shead p", "rv", 140);
        mark("#agent", "rv-scale", 180);
        mark("footer .r1", "rv", 0);
        mark("footer .r2", "rv", 120);
        mark("footer .legal", "rv", 220);

        const targets = new Map();
        const io = new IntersectionObserver(
          es => {
            es.forEach(e => {
              if (e.isIntersecting) {
                (targets.get(e.target) || []).forEach(el => el.classList.add("in"));
                io.unobserve(e.target);
              }
            });
          },
          { rootMargin: "0px 0px 80px 0px", threshold: 0.02 }
        );

        document.querySelectorAll(".rv, .rv-scale, .rv-pop, .rv-clip, .rv-date").forEach(el => {
          const t = el.classList.contains("rv-clip") ? el.parentNode : el;
          if (!targets.has(t)) targets.set(t, []);
          targets.get(t).push(el);
          io.observe(t);
        });

        // Dedicated observer for footer to guarantee bottom legal text and links always reveal
        const footerEl = document.querySelector("footer");
        if (footerEl) {
          const footerIo = new IntersectionObserver(
            es => {
              es.forEach(e => {
                if (e.isIntersecting) {
                  document.querySelectorAll("footer .rv").forEach(el => el.classList.add("in"));
                  footerIo.unobserve(e.target);
                }
              });
            },
            { rootMargin: "0px 0px 120px 0px", threshold: 0.01 }
          );
          footerIo.observe(footerEl);
        }
      });
    }
  }
};
</script>

<style scoped>
/* =====================================================================
   TOKENS & RESET
   ===================================================================== */
.teaser-page {
  /* brand colours (guidelines p.26) */
  --jameson-green: #008557;       /* 68% — доминанта */
  --jameson-dark-green: #00483A;  /* 15% */
  --jameson-cream: #FFF8E5;       /* 15% */
  --jameson-red: #A02136;         /* ≤2% — только ошибки */
  --jameson-gold: linear-gradient(135deg, #C6A15B 0%, #E4C88A 45%, #B88A3E 100%); /* симуляция золота для digital */

  /* semantic */
  --color-bg: var(--jameson-cream);
  --color-surface: var(--jameson-cream);
  --color-surface-2: #F3EAD2;      /* тон пузыря ассистента на кремовом */
  --color-text: var(--jameson-dark-green);
  --color-text-muted: #3F5F56;
  --color-on-dark: var(--jameson-cream);
  --color-on-dark-muted: #D9E8DF;
  --color-border: rgba(0, 72, 58, 0.18);
  --color-border-strong: rgba(0, 72, 58, 0.55);
  --color-danger: var(--jameson-red);
  --color-success: var(--jameson-green);

  /* type: JJ */
  --font: "JJ", "Helvetica Neue", Arial, sans-serif;
  --fs-14: 14px;
  --fs-16: 16px;
  --fs-18: 18px;
  --fs-24: 24px;
  --fs-32: 32px;
  --fs-40: 40px;
  --fs-display: 64px;
  --track-head: -0.005em;
  --lh-head: 1;

  /* space: 8pt */
  --s-1: 8px;
  --s-2: 16px;
  --s-3: 24px;
  --s-4: 32px;
  --s-5: 40px;
  --s-6: 48px;
  --s-8: 64px;
  --s-10: 80px;
  --s-12: 96px;

  --r-card: 16px;
  --r-bubble: 24px;
  --r-pill: 999px;
  --hit: 48px;
  --wrap: 1120px;

  --how-bg: var(--jameson-cream);
  --how-text: var(--jameson-dark-green);
  --how-muted: #3F5F56;
  --how-border: rgba(0, 72, 58, 0.18);
  --how-num: var(--jameson-green);
  --how-btn-bg: var(--jameson-dark-green);
  --how-btn-fg: var(--jameson-cream);

  font-family: var(--font);
  font-size: var(--fs-16);
  line-height: 24px;
  background: var(--color-bg);
  color: var(--color-text);
  margin: 0;
  padding: 0;
  -webkit-font-smoothing: antialiased;
  min-height: 100vh;
}

@media (prefers-color-scheme: dark) {
  :root:not([data-theme="light"]),
  .teaser-page:not([data-theme="light"]) {
    --how-bg: var(--jameson-dark-green);
    --how-text: var(--jameson-cream);
    --how-muted: #D9E8DF;
    --how-border: rgba(255, 248, 229, 0.22);
    --how-num: #D9E8DF;
    --how-btn-bg: var(--jameson-cream);
    --how-btn-fg: var(--jameson-dark-green);
  }
}

:root[data-theme="dark"],
.teaser-page[data-theme="dark"] {
  --how-bg: var(--jameson-dark-green);
  --how-text: var(--jameson-cream);
  --how-muted: #D9E8DF;
  --how-border: rgba(255, 248, 229, 0.22);
  --how-num: #D9E8DF;
  --how-btn-bg: var(--jameson-cream);
  --how-btn-fg: var(--jameson-dark-green);
}

.teaser-page,
.teaser-page * {
  box-sizing: border-box;
}

.teaser-page,
.teaser-page button,
.teaser-page input,
.teaser-page select,
.teaser-page textarea,
.teaser-page a,
.teaser-page p,
.teaser-page span,
.teaser-page h1,
.teaser-page h2,
.teaser-page h3,
.teaser-page label {
  font-family: var(--font) !important;
}

.teaser-page h1,
.teaser-page h2,
.teaser-page h3 {
  margin: 0;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--track-head);
}

.teaser-page p {
  margin: 0;
}


.wrap {
  max-width: var(--wrap);
  margin: 0 auto;
  padding: 0 var(--s-3);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

:where(a, button, input, select, [tabindex]):focus-visible {
  outline: 3px solid var(--jameson-dark-green);
  outline-offset: 3px;
  border-radius: 4px;
}
.on-dark :where(a, button, input, select, [tabindex]):focus-visible {
  outline-color: var(--jameson-cream);
}

/* LOGO */
.logo {
  display: block;
  width: 150px;
  aspect-ratio: 1200 / 279;
  -webkit-mask: url("~@/assets/img/teaser-logo.webp") center / contain no-repeat;
  mask: url("~@/assets/img/teaser-logo.webp") center / contain no-repeat;
  background: var(--jameson-cream);
}
.logo.positive {
  background: var(--jameson-dark-green);
}

/* BUTTONS */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--s-1);
  min-height: var(--hit);
  padding: 0 var(--s-4);
  font-family: "JJ", "jj2bold", "OswaldBold", sans-serif !important;
  font-weight: 700;
  font-size: var(--fs-16);
  line-height: 24px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  text-decoration: none;
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: 0;
  transition: background-color 0.15s ease, color 0.15s ease;
  text-align: center;
  user-select: none;
}
.btn-lg {
  min-height: 64px;
  padding: 0 var(--s-5);
}
@media (max-width: 559px) {
  .btn {
    letter-spacing: 0.06em;
    padding-left: var(--s-2);
    padding-right: var(--s-2);
  }
}
.teaser-page .btn-cream,
.teaser-page a.btn-cream,
.teaser-page button.btn-cream,
.btn-cream {
  background-color: var(--jameson-cream) !important;
  color: #00483A !important;
  text-decoration: none !important;
}
.teaser-page .btn-cream:hover,
.teaser-page a.btn-cream:hover,
.teaser-page button.btn-cream:hover,
.teaser-page .btn-cream:focus,
.teaser-page a.btn-cream:focus,
.teaser-page button.btn-cream:focus,
.btn-cream:hover {
  background-color: #FFFFFF !important;
  color: #002820 !important;
  text-decoration: none !important;
}
.teaser-page .btn-outline,
.teaser-page a.btn-outline,
.teaser-page button.btn-outline,
.btn-outline {
  background-color: transparent !important;
  color: var(--jameson-cream) !important;
  border-color: var(--jameson-cream) !important;
  text-decoration: none !important;
}
.teaser-page .btn-outline:hover,
.teaser-page a.btn-outline:hover,
.teaser-page button.btn-outline:hover,
.teaser-page .btn-outline:focus,
.teaser-page a.btn-outline:focus,
.teaser-page button.btn-outline:focus,
.btn-outline:hover {
  background-color: rgba(255, 248, 229, 0.15) !important;
  color: #FFFFFF !important;
  border-color: #FFFFFF !important;
  text-decoration: none !important;
}
.teaser-page .btn-dark,
.teaser-page a.btn-dark,
.teaser-page button.btn-dark,
.btn-dark {
  background-color: var(--jameson-dark-green) !important;
  color: var(--jameson-cream) !important;
  text-decoration: none !important;
}
.teaser-page .btn-dark:hover,
.teaser-page a.btn-dark:hover,
.teaser-page button.btn-dark:hover,
.btn-dark:hover {
  background-color: #003A2F !important;
  color: #FFFFFF !important;
  text-decoration: none !important;
}
.teaser-page .btn-how,
.teaser-page a.btn-how,
.teaser-page button.btn-how,
.btn-how {
  background-color: var(--how-btn-bg) !important;
  color: var(--how-btn-fg) !important;
  text-decoration: none !important;
}
.teaser-page .btn-how:hover,
.teaser-page a.btn-how:hover,
.teaser-page button.btn-how:hover,
.btn-how:hover {
  opacity: 0.9 !important;
  text-decoration: none !important;
}
.btn[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
}
.btn[aria-busy="true"] {
  cursor: progress;
}

/* HEADER */
.header {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 5;
}
.header-fade-enter-active {
  transition: opacity 0.35s ease;
}
.header-fade-enter {
  opacity: 0;
}
.header .in {
  max-width: var(--wrap);
  margin: 0 auto;
  padding: var(--s-3);
  padding-top: max(var(--s-3), calc(env(safe-area-inset-top, 0px) + var(--s-3)));
  padding-right: max(var(--s-3), calc(env(safe-area-inset-right, 0px) + var(--s-3)));
  padding-left: max(var(--s-3), calc(env(safe-area-inset-left, 0px) + var(--s-3)));
  display: flex;
  align-items: center;
  gap: var(--s-3);
  box-sizing: border-box;
}
.header .right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: var(--s-1);
}
.lang {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(0, 48, 38, 0.45);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(241, 228, 178, 0.25);
  border-radius: 999px;
  padding: 3px 5px;
  box-sizing: border-box;
}
.lang a {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px 12px;
  min-width: 38px;
  height: 30px;
  line-height: 1;
  font-family: "JJ", "Oswald", "Montserrat", sans-serif;
  font-size: var(--fs-14);
  letter-spacing: 0.08em;
  text-decoration: none;
  color: rgba(242, 228, 181, 0.7);
  border-radius: 999px;
  transition: all 0.2s ease;
  box-sizing: border-box;
}
.lang a[aria-current="true"] {
  color: #00483A;
  background: #F1E4B2;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}

/* HERO */
.hero {
  --kv-grad: linear-gradient(to bottom, #004636 0%, #004636 10%, #004a39 20%, #00503d 30%, #005b44 40%, #00654c 50%, #006c51 60%, #006b51 70%, #00674d 80%, #00644c 90%, #00684f 100%);
  position: relative;
  background: var(--kv-grad);
  color: var(--jameson-cream);
  overflow: hidden;
}
.hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) min(50vw, 760px);
  align-items: stretch;
}
.hero-text {
  align-self: center;
  padding: calc(var(--s-12) + var(--s-2)) var(--s-5) var(--s-12) max(var(--s-3), calc((100vw - var(--wrap)) / 2 + var(--s-3)));
  position: relative;
  z-index: 1;
}
.hero h1 {
  font-size: clamp(34px, 3.3vw, 54px);
  line-height: var(--lh-head);
  margin-bottom: var(--s-3);
  text-wrap: balance;
  color: var(--jameson-cream);
}
.hero .lead {
  font-size: var(--fs-24);
  line-height: 32px;
  margin-bottom: var(--s-5);
  color: var(--jameson-cream);
}
.tt-title {
  display: block;
  width: 100%;
  max-width: 560px;
  height: auto;
  margin-bottom: var(--s-3);
}
.h1-text {
  display: block;
}
.hero .actions {
  display: flex;
  gap: var(--s-2);
  flex-wrap: wrap;
}

/* HERO MEDIA & KV STAGE */
.kv-stage {
  position: relative;
  width: 100%;
  aspect-ratio: 1840 / 2400;
  overflow: hidden;
  background: var(--kv-grad);
  --sig-color: #FFF8E5;
}
.kv-layers {
  position: absolute;
  inset: 0;
  -webkit-mask-image: linear-gradient(to right, transparent 0, #000 12%), linear-gradient(to bottom, transparent 0, #000 6%, #000 90%, transparent 100%);
  -webkit-mask-composite: source-in;
  mask-image: linear-gradient(to right, transparent 0, #000 12%), linear-gradient(to bottom, transparent 0, #000 6%, #000 90%, transparent 100%);
  mask-composite: intersect;
}
.kv-layers img {
  position: absolute;
  left: -4.3478%;
  top: -3.3333%;
  width: 108.6957%;
  height: auto;
  max-width: none;
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;
  will-change: transform;
  transform: translate3d(0, 0, 0);
}
.kv-layers.intro > .kv-layer {
  opacity: 0;
}
.kv-layers.intro > .kv-l-bg {
  opacity: 1;
}
.kv-layer {
  position: absolute;
  inset: 0;
  will-change: transform;
  transition: opacity 0.8s ease;
}
.kv-j {
  position: absolute;
  left: 21%;
  top: 19%;
  width: 58%;
  height: auto;
  fill: var(--sig-color);
  overflow: visible;
}
.kv-pattern {
  position: absolute;
  left: -8%;
  top: -8%;
  width: 116%;
  height: 116%;
  fill: #00483A;
  opacity: 0.5;
  -webkit-mask-image: radial-gradient(ellipse 48% 42% at 50% 48%, transparent 0%, transparent 35%, #000 100%);
  mask-image: radial-gradient(ellipse 48% 42% at 50% 48%, transparent 0%, transparent 35%, #000 100%);
}
.kv-glow {
  position: absolute;
  left: 50%;
  top: 48%;
  width: 88%;
  aspect-ratio: 1;
  transform: translate(-50%, -50%);
  background: radial-gradient(closest-side, rgba(11, 163, 107, 0.5), rgba(11, 163, 107, 0.16) 55%, rgba(11, 163, 107, 0));
}

.scroll-cue {
  position: absolute;
  left: max(var(--s-3), calc((100vw - var(--wrap)) / 2 + var(--s-3)));
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--s-2);
  text-decoration: none;
  color: var(--jameson-cream);
  font-size: var(--fs-14);
  letter-spacing: 0.16em;
  text-transform: uppercase;
  z-index: 2;
}
.scroll-cue i {
  display: block;
  width: 2px;
  height: 56px;
  background: var(--jameson-cream);
  margin-left: 2px;
}
.scroll-cue:hover {
  color: #fff;
}

@media (max-width: 959px) {
  .hero {
    background: #00684f;
  }
  .hero-grid {
    grid-template-columns: 1fr;
  }
  .hero-media {
    order: -1;
    padding-top: 64px;
    background: #004636;
  }
  .kv-stage {
    aspect-ratio: 1 / 1;
  }
  .kv-layers img {
    top: -17.39%;
  }
  .kv-j {
    left: 21%;
    top: 11%;
    width: 58%;
  }
  .kv-layers {
    -webkit-mask-image: linear-gradient(to bottom, transparent 0, #000 10%, #000 82%, transparent 100%);
    mask-image: linear-gradient(to bottom, transparent 0, #000 10%, #000 82%, transparent 100%);
  }
  .kv-glow {
    width: 80%;
  }
  .hero-text {
    padding: var(--s-1) var(--s-3) var(--s-8);
  }
  .tt-title {
    max-width: 74%;
    margin-bottom: 12px;
  }
  .hero h1 {
    font-size: 28px;
    line-height: var(--lh-head);
    margin-bottom: 12px;
  }
  .hero .lead {
    font-size: var(--fs-18);
    line-height: 28px;
    margin-bottom: var(--s-3);
  }
  .hero .actions .btn {
    flex: 1 1 100%;
  }
  .scroll-cue {
    display: none;
  }
}

/* HOW SECTION */
.how {
  padding: var(--s-12) 0;
  background: var(--how-bg);
  color: var(--how-text);
}
.how .shead h2 {
  color: var(--how-text);
}
.how .shead p {
  color: var(--how-muted);
}
.shead {
  max-width: 640px;
  margin-bottom: var(--s-6);
}
.shead h2 {
  font-size: var(--fs-40);
  line-height: var(--lh-head);
  margin-bottom: var(--s-2);
}
.shead p {
  font-size: var(--fs-18);
  line-height: 32px;
  color: var(--color-text-muted);
}
.steps {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--s-3);
  list-style: none;
  margin: 0;
  padding: 0;
}
@media (max-width: 760px) {
  .steps {
    grid-template-columns: 1fr;
  }
}
.step {
  border: 1px solid var(--how-border);
  border-radius: var(--r-card);
  padding: var(--s-5) var(--s-4);
  display: flex;
  flex-direction: column;
  background: var(--how-bg);
}
.step .ico {
  width: 88px;
  height: 88px;
  margin-bottom: var(--s-3);
}
.ico-par {
  display: block;
  width: 88px;
  height: 88px;
  margin-bottom: var(--s-3);
  will-change: transform;
}
.step .ico-par .ico {
  display: block;
  margin: 0;
}
.step .num {
  font-size: var(--fs-14);
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--how-num);
  font-weight: 700;
  margin-bottom: var(--s-1);
}
.step h3 {
  font-size: var(--fs-32);
  line-height: var(--lh-head);
  margin-bottom: var(--s-2);
  color: var(--how-text);
}
.step p {
  color: var(--how-muted);
  font-size: var(--fs-18);
  line-height: 28px;
}
.step .foot {
  margin-top: auto;
  padding-top: var(--s-4);
}
@media (max-width: 759px) {
  .step .foot .btn {
    width: 100%;
    padding: 0 var(--s-2);
  }
}
.step .date {
  font-size: var(--fs-32);
  line-height: 32px;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--how-text);
  letter-spacing: 0;
}

/* SCROLL REVEAL ANIMATIONS */
.teaser-page.anim .rv {
  opacity: 0;
  transform: translate3d(0, 32px, 0);
}
.teaser-page.anim .rv-scale {
  opacity: 0;
  transform: translate3d(0, 48px, 0) scale(0.96);
}
.teaser-page.anim .rv-pop {
  opacity: 0;
  transform: scale(0.4);
}
.teaser-page.anim .rv-clip {
  clip-path: inset(0 0 100% 0);
  transform: translate3d(0, 24px, 0);
}
.teaser-page.anim .rv-date {
  opacity: 0;
  letter-spacing: 0.32em;
}
.teaser-page.anim :is(.rv, .rv-scale, .rv-clip) {
  transition: opacity 0.7s cubic-bezier(0.2, 0.7, 0.2, 1) var(--d, 0ms),
              transform 0.9s cubic-bezier(0.2, 0.7, 0.2, 1) var(--d, 0ms),
              clip-path 0.9s cubic-bezier(0.2, 0.7, 0.2, 1) var(--d, 0ms);
}
.teaser-page.anim .rv-pop {
  transition: opacity 0.35s ease var(--d, 0ms),
              transform 0.75s cubic-bezier(0.34, 1.56, 0.64, 1) var(--d, 0ms);
}
.teaser-page.anim .rv-date {
  transition: opacity 0.8s ease var(--d, 0ms),
              letter-spacing 1.2s cubic-bezier(0.2, 0.7, 0.2, 1) var(--d, 0ms);
}
.teaser-page.anim :is(.rv, .rv-scale, .rv-pop).in {
  opacity: 1;
  transform: none;
}
.teaser-page.anim .rv-clip.in {
  clip-path: inset(-25% -5% -25% -5%);
  transform: none;
}
.teaser-page.anim .rv-date.in {
  opacity: 1;
  letter-spacing: 0;
}

/* APPLY SECTION */
.apply {
  background: var(--jameson-green);
  padding: var(--s-12) 0;
  color: var(--jameson-cream);
}
.apply .shead p {
  color: var(--color-on-dark-muted);
}
.apply .shead h2 {
  color: var(--jameson-cream);
}

.agent {
  max-width: 760px;
  background: var(--jameson-cream);
  color: var(--jameson-dark-green);
  --color-surface: var(--jameson-cream);
  --color-text: var(--jameson-dark-green);
  --color-text-muted: #3F5F56;
  --color-border: rgba(0, 72, 58, 0.18);
  --color-border-strong: rgba(0, 72, 58, 0.55);
  border-radius: var(--r-card);
  overflow: hidden;
}
.agent-head {
  display: flex;
  align-items: center;
  gap: var(--s-2);
  padding: var(--s-2) var(--s-3);
  background: var(--jameson-dark-green);
  color: var(--jameson-cream);
}
.agent-head .logo {
  width: 96px;
}
.agent-head .t {
  font-size: var(--fs-14);
  line-height: 16px;
  color: var(--color-on-dark-muted);
}
.agent-progress {
  margin-left: auto;
  text-align: right;
  min-width: 140px;
  flex-shrink: 0;
  display: block !important;
  height: auto !important;
  background: transparent !important;
  border-radius: 0 !important;
  overflow: visible !important;
}
.agent-progress-lbl {
  font-size: var(--fs-14);
  line-height: 20px;
  font-weight: 700;
  color: var(--jameson-cream);
  font-family: var(--font-family-book);
  margin-bottom: 6px;
  letter-spacing: 0.02em;
}
.agent-progress-bar {
  height: 6px;
  background: rgba(255, 248, 229, 0.2);
  border-radius: 3px;
  overflow: hidden;
  position: relative;
  width: 100%;
}
.agent-progress-bar i {
  display: block;
  height: 100%;
  background: var(--jameson-gold);
  border-radius: 3px;
  width: 0;
  transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}
.agent-progress-bar i.complete {
  background: #E4C88A;
  box-shadow: 0 0 10px rgba(228, 200, 138, 0.7);
}
@media (max-width: 560px) {
  .agent-head {
    padding: 10px 14px;
    gap: 8px;
  }
  .agent-head .logo {
    width: 80px;
  }
  .agent-head .t {
    display: none;
  }
  .agent-progress {
    min-width: 110px;
  }
}

/* CLASSIC FORM */
.classic {
  padding: var(--s-3);
}
.grid2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--s-2);
}
@media (max-width: 560px) {
  .grid2 {
    grid-template-columns: 1fr;
  }
}
.cf {
  display: flex;
  flex-direction: column;
  gap: var(--s-1);
}
.cf.full {
  grid-column: 1 / -1;
}
.cf > label,
.cf > .lbl {
  font-size: var(--fs-14);
  font-weight: 700;
  color: var(--jameson-dark-green);
}
.cf select.field {
  appearance: none;
  -webkit-appearance: none;
  padding-right: 44px;
  background: #FFFDF6 url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='9' viewBox='0 0 14 9'%3E%3Cpath d='M1 1l6 6 6-6' fill='none' stroke='%2300483A' stroke-width='2'/%3E%3C/svg%3E") no-repeat right 16px center;
  cursor: pointer;
}
.cf .seg {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--s-1);
}
.cf .seg label {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: var(--hit);
  border: 2px solid var(--color-border-strong);
  background: #FFFDF6;
  color: var(--jameson-dark-green);
  cursor: pointer;
  font-weight: 700;
  border-radius: 0;
}
.cf .seg input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.cf .seg label:has(input:checked) {
  background: var(--jameson-dark-green);
  color: var(--jameson-cream);
  border-color: var(--jameson-dark-green);
}
.cf .seg label:has(input:focus-visible) {
  outline: 3px solid var(--jameson-dark-green);
  outline-offset: 3px;
}
.cf.bad .field,
.cf.bad .seg label {
  border-color: var(--color-danger);
}
.cf .err {
  margin-top: 0;
}

/* FIELDS & INPUTS */
.field {
  flex: 1;
  width: 100%;
  min-height: var(--hit);
  padding: 0 var(--s-2);
  border: 2px solid var(--color-border-strong);
  border-radius: 0;
  font-family: var(--font);
  font-size: var(--fs-16);
  color: var(--jameson-dark-green);
  background: #FFFDF6;
}
.field::placeholder {
  color: #6E847D;
}
.field:focus {
  border-color: var(--jameson-dark-green);
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 133, 87, 0.25);
}
.field[aria-invalid="true"] {
  border-color: var(--color-danger);
}
.err {
  display: none;
  margin-top: var(--s-1);
  font-size: var(--fs-14);
  line-height: 24px;
  color: var(--color-danger);
}
.err.on {
  display: block;
}

/* CONSENT */
.consent {
  display: flex;
  gap: var(--s-2);
  align-items: flex-start;
  padding: var(--s-2);
  border: 2px solid var(--color-border-strong);
  cursor: pointer;
  margin-bottom: var(--s-2);
  background: #FFFDF6;
  border-radius: 0;
}
.consent[data-invalid="true"] {
  border-color: var(--color-danger);
}
.consent input {
  width: 24px;
  height: 24px;
  margin: 0;
  accent-color: var(--jameson-dark-green);
  flex: 0 0 auto;
}
.consent span {
  font-size: var(--fs-14);
  line-height: 24px;
  color: #3F5F56;
}
.consent a {
  color: var(--jameson-dark-green);
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  transition: opacity 0.2s ease;
}
.consent a:hover {
  opacity: 0.8;
}

/* ALERT */
.alert {
  display: flex;
  gap: var(--s-2);
  align-items: flex-start;
  padding: var(--s-2);
  background: #FBEDEF;
  border: 2px solid var(--color-danger);
  margin-bottom: var(--s-2);
  border-radius: 0;
}
.alert svg {
  width: 24px;
  height: 24px;
  color: var(--color-danger);
  flex: 0 0 auto;
}
.alert b {
  display: block;
}
.alert p {
  font-size: var(--fs-14);
  line-height: 24px;
}

/* MODAL POPUP */
.done-modal {
  position: fixed;
  inset: 0;
  z-index: 3000 !important; /* Находится поверх всех оверлеев и OTP */
  display: flex !important;
  align-items: center;
  justify-content: center;
  padding: var(--s-3);
  box-sizing: border-box;
}
.done-modal[hidden] {
  display: none !important;
}
.done-modal-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 30, 22, 0.82);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 1 !important;
}
.done-modal-card {
  position: relative;
  z-index: 10 !important; /* Гарантированно выше бэкдропа и не размывается */
  width: 100%;
  max-width: 480px;
  background: var(--jameson-cream) !important;
  color: var(--jameson-dark-green) !important;
  border-radius: var(--r-card);
  padding: var(--s-5) var(--s-4) var(--s-4);
  text-align: center;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55), 0 4px 12px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(242, 228, 181, 0.5);
}
.done-modal-card .done-ok {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--jameson-green);
  color: var(--jameson-cream);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--s-3);
  box-shadow: 0 8px 20px rgba(0, 133, 87, 0.3);
}
.done-modal-card .done-ok svg {
  width: 32px;
  height: 32px;
  stroke: var(--jameson-cream);
}
.done-modal-card h3 {
  font-family: var(--font-family-bold);
  font-size: var(--fs-32);
  line-height: var(--lh-head);
  margin-bottom: var(--s-2);
  color: var(--jameson-dark-green);
  text-transform: uppercase;
}
.done-modal-card p {
  color: #3F5F56;
  font-size: var(--fs-16);
  line-height: 24px;
  font-family: var(--font-family-book);
}
.done-modal-card .done-region {
  margin-top: var(--s-2);
  color: var(--jameson-dark-green);
  font-weight: 700;
  font-size: var(--fs-18);
  font-family: var(--font-family-bold);
}
.done-modal-card .done-modal-ok {
  width: 100%;
  margin-top: var(--s-4);
}
.done-modal-x {
  position: absolute;
  top: var(--s-2);
  right: var(--s-2);
  width: var(--hit);
  height: var(--hit);
  border: 0;
  background: none;
  color: var(--jameson-dark-green);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s ease;
  z-index: 12;
}
.done-modal-x:hover {
  background: rgba(0, 70, 54, 0.1);
}
.done-modal-x svg {
  width: 22px;
  height: 22px;
}

/* Modal Vue Transitions */
.teaser-modal-enter-active,
.teaser-modal-leave-active {
  transition: opacity 0.35s ease;
}
.teaser-modal-enter-active .done-modal-card,
.teaser-modal-leave-active .done-modal-card {
  transition: transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1);
}
.teaser-modal-enter,
.teaser-modal-leave-to {
  opacity: 0;
}
.teaser-modal-enter .done-modal-card,
.teaser-modal-leave-to .done-modal-card {
  transform: scale(0.92);
}

/* =====================================================================
   LEGAL POPUP (Policy, Terms, Offer, Rules)
   ===================================================================== */
.legal-popup-overlay {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 18, 12, 0.82);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2800 !important;
  padding: 20px;
  box-sizing: border-box;
}

.legal-popup-content {
  background: radial-gradient(130% 120% at 50% 0%, #0d402c 0%, #082d1f 55%, #031a12 100%);
  border: 1px solid rgba(242, 228, 181, 0.35);
  border-radius: 24px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.65), inset 0 1px 0 rgba(255, 248, 229, 0.15);
  padding: 32px 32px 28px;
  width: 100%;
  max-width: 960px;
  height: 85vh;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  position: relative;
}

.legal-popup-header {
  padding-bottom: 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(242, 228, 181, 0.2);
  flex-shrink: 0;
}

.legal-header-text {
  font-family: "JJ", "jj2bold", "OswaldBold", sans-serif;
  font-weight: 700;
  font-size: 22px;
  line-height: 1.25;
  text-transform: uppercase;
  color: #F2E4B5;
  letter-spacing: 0.04em;
  margin: 0;
  padding-right: 12px;
}

.legal-popup-close {
  background: rgba(242, 228, 181, 0.08);
  border: 1px solid rgba(242, 228, 181, 0.2);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #F2E4B5;
  transition: all 0.2s ease;
  flex-shrink: 0;
  outline: none;
  padding: 0;
}

.legal-popup-close:hover {
  background: rgba(242, 228, 181, 0.22);
  border-color: rgba(242, 228, 181, 0.45);
  transform: rotate(90deg) scale(1.05);
}

.legal-popup-body {
  font-family: "JJ", "jj2book", "HelveticaNeueCyr", sans-serif;
  font-weight: 400;
  font-size: 15px;
  color: #F2E4B5;
  line-height: 1.65;
  white-space: pre-line;
  padding: 22px 14px 10px 0;
  overflow-y: auto;
  flex: 1;
}

.legal-popup-body a {
  color: #F2E4B5;
  text-decoration: underline;
  text-underline-offset: 3px;
  font-weight: 500;
  transition: opacity 0.2s ease;
}

.legal-popup-body a:hover {
  opacity: 0.8;
}

.legal-popup-body b {
  color: #FFFFFF;
  font-weight: 700;
}

.legal-popup-body::-webkit-scrollbar {
  width: 6px;
}

.legal-popup-body::-webkit-scrollbar-thumb {
  background-color: #F2E4B5;
  border-radius: 4px;
}

.legal-popup-body::-webkit-scrollbar-track {
  background: rgba(242, 228, 181, 0.1);
  border-radius: 4px;
}

.legal-popup-enter-active,
.legal-popup-leave-active {
  transition: opacity 0.3s ease;
}

.legal-popup-enter-active .legal-popup-content,
.legal-popup-leave-active .legal-popup-content {
  transition: transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.legal-popup-enter,
.legal-popup-leave-to {
  opacity: 0;
}

.legal-popup-enter .legal-popup-content,
.legal-popup-leave-to .legal-popup-content {
  transform: translateY(-16px) scale(0.97);
}

@media (max-width: 768px) {
  .legal-popup-overlay {
    padding: 12px;
  }
  .legal-popup-content {
    padding: 24px 20px 20px;
    border-radius: 16px;
    height: 90vh;
    max-height: 90vh;
  }
  .legal-header-text {
    font-size: 16px;
  }
  .legal-popup-close {
    width: 34px;
    height: 34px;
  }
  .legal-popup-body {
    font-size: 13px;
    padding-top: 16px;
  }
}

/* FOOTER */
footer {
  background: var(--jameson-dark-green);
  color: var(--jameson-cream);
  padding: var(--s-8) 0 calc(var(--s-6) + env(safe-area-inset-bottom, 0px));
}
footer .r1 {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: var(--s-4);
  flex-wrap: wrap;
  padding-bottom: var(--s-4);
  border-bottom: 1px solid rgba(255, 248, 229, 0.2);
}
footer .logo {
  width: 220px;
}
footer .resp {
  font-size: var(--fs-32);
  line-height: var(--lh-head);
  text-wrap: balance;
  font-weight: 700;
  text-transform: uppercase;
  max-width: 18ch;
  text-align: right;
}
@media (max-width: 700px) {
  footer .resp {
    text-align: left;
  }
}
footer .r2 {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--s-3);
  flex-wrap: wrap;
  padding-top: var(--s-3);
}
footer .links {
  display: flex;
  flex-wrap: wrap;
  gap: 0 var(--s-2);
}
footer .links a {
  display: inline-flex;
  align-items: center;
  min-height: var(--hit);
  font-size: var(--fs-14);
  letter-spacing: 0.06em;
  color: var(--color-on-dark-muted);
  text-decoration: underline;
  text-underline-offset: 4px;
}
footer .links a:hover {
  color: var(--jameson-cream);
}
.ig {
  width: var(--hit);
  height: var(--hit);
  border: 1px solid rgba(255, 248, 229, 0.5);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--jameson-cream);
}
.ig:hover {
  background: var(--jameson-cream);
  color: var(--jameson-dark-green);
}
.ig svg {
  width: 20px;
  height: 20px;
}
footer .legal {
  margin-top: var(--s-3);
  font-size: var(--fs-14);
  line-height: 24px;
  color: var(--color-on-dark-muted);
}

.teaser-page.anim footer .rv.in {
  opacity: 1 !important;
  transform: none !important;
}

@media (prefers-reduced-motion: reduce) {
  .teaser-page * {
    transition: none !important;
    animation: none !important;
  }
  .done-modal-backdrop,
  .done-modal-card {
    animation: none;
  }
  .kv-layers.intro > .kv-layer {
    opacity: 1;
  }
}
</style>
